import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useFiliale } from '../context/FilialeContext';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import { 
  FileSpreadsheet, 
  Download, 
  Calendar, 
  Filter, 
  Printer,
  FileText,
  Wallet,
  Users,
  Building2,
  CheckCircle,
  AlertCircle,
  Check,
  RefreshCw,
  RotateCcw,
  LayoutGrid,
  Landmark,
  User,
  Briefcase,
  Clock,
  Gift,
  CreditCard,
  Receipt,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Table,
  Eye,
  Info,
  Link2,
  GripVertical,
  Calculator,
  Pencil,
  Phone,
  GraduationCap,
  Save,
  GitBranch,
  Sparkles,
  Archive
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════
//  PRÉSENTATION — helpers et composants purement visuels
//  Aucune logique métier ni appel API ici : tout arrive par les props.
// ═══════════════════════════════════════════════════════════════════════

// Normalise un code filiale (ex. 'gn' -> 'GN') : évite les écarts de casse / d'espaces
const normaliserCode = (code) => (code ?? '').toString().trim().toUpperCase();

const formatDateFr = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
};

// Catégories d'affichage des rapports (les onglets sans rapport disponible sont masqués)
const CATEGORIES = [
  { id: 'all', label: 'Tous', icon: LayoutGrid },
  { id: 'paie', label: 'Paie & heures', icon: Wallet },
  { id: 'charges', label: 'Charges', icon: Landmark },
  { id: 'declarations', label: 'Déclarations', icon: Building2 },
];
const CATEGORIE_RAPPORT = {
  bulletin_paie: 'paie',
  etat_salarial: 'paie',
  decompte_heures: 'paie',
  etat_charges: 'charges',
  declaration_cnss: 'declarations',
  declaration_cnps: 'declarations',
  declaration_vrsmt: 'declarations',
};
const TONE_CATEGORIE = {
  paie: 'from-blue-500 to-indigo-600',
  charges: 'from-amber-500 to-orange-600',
  declarations: 'from-emerald-500 to-teal-600',
};
const ETATS_PAIE = {
  0: { label: 'Non active', classe: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200' },
  1: { label: 'Ouverte', classe: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  2: { label: 'Clôturée', classe: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' },
};

const INPUT =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 shadow-sm shadow-slate-900/[0.03] transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10';
const LABEL = 'mb-1.5 block text-[13px] font-semibold text-slate-600';

const PaieStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    @keyframes paieFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
    @keyframes paieFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes paiePop { 0% { transform: scale(.4); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
    @keyframes paieSlide { 0% { transform: translateX(-110%); } 100% { transform: translateX(310%); } }
    @keyframes paieModal { from { opacity: 0; transform: translateY(14px) scale(.96); } to { opacity: 1; transform: none; } }

    .paie-fade-up { animation: paieFadeUp .45s cubic-bezier(.2,.8,.2,1) both; }
    .paie-fade { animation: paieFade .25s ease-out both; }
    .paie-pop { animation: paiePop .35s cubic-bezier(.2,.8,.2,1) both; }
    .paie-slide { animation: paieSlide 1.3s ease-in-out infinite; }
    .paie-modal { animation: paieModal .28s cubic-bezier(.2,.8,.2,1) both; }

    /* Fond de page : lumière douce + grain léger */
    .paie-root {
      font-family: 'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
      -webkit-font-smoothing: antialiased;
      background:
        radial-gradient(900px 420px at 92% -8%, rgb(99 102 241 / .10), transparent 65%),
        radial-gradient(800px 380px at -8% 4%, rgb(14 165 233 / .08), transparent 65%),
        #f5f6fa;
    }
    .paie-root ::selection { background: rgb(99 102 241 / .18); }
    .paie-root .tabular-nums { font-variant-numeric: tabular-nums; }

    /* Carte : bordure fine + ombres en couches */
    .paie-card {
      background: #fff;
      border-radius: 1.125rem;
      box-shadow: 0 0 0 1px rgb(15 23 42 / .06), 0 1px 2px rgb(15 23 42 / .04), 0 14px 34px -18px rgb(15 23 42 / .16);
    }

    /* Bandeau et dock : bleu nuit profond, quadrillage discret */
    .paie-hero {
      background:
        radial-gradient(600px 220px at 100% 0%, rgb(129 140 248 / .38), transparent 70%),
        radial-gradient(500px 200px at 0% 100%, rgb(56 189 248 / .22), transparent 70%),
        linear-gradient(135deg, #0f1f4d 0%, #1e3a8a 55%, #3730a3 100%);
    }
    .paie-hero::after {
      content: ''; position: absolute; inset: 0; pointer-events: none;
      background-image: linear-gradient(rgb(255 255 255 / .06) 1px, transparent 1px), linear-gradient(90deg, rgb(255 255 255 / .06) 1px, transparent 1px);
      background-size: 28px 28px;
      -webkit-mask-image: linear-gradient(to left, #000, transparent 70%);
      mask-image: linear-gradient(to left, #000, transparent 70%);
    }
    .paie-dock {
      background: linear-gradient(135deg, #0f1f4d 0%, #1e3a8a 60%, #312e81 100%);
      box-shadow: 0 24px 48px -20px rgb(15 31 77 / .65), inset 0 0 0 1px rgb(255 255 255 / .07);
    }

    /* Reflet au survol du bouton principal */
    .paie-shine { position: relative; overflow: hidden; }
    .paie-shine::before {
      content: ''; position: absolute; inset: 0; pointer-events: none;
      background: linear-gradient(110deg, transparent 35%, rgb(37 99 235 / .12) 50%, transparent 65%);
      transform: translateX(-120%); transition: transform .8s ease;
    }
    .paie-shine:hover::before { transform: translateX(120%); }

    /* Barres de défilement fines */
    .paie-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
    .paie-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
    .paie-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; border: 2px solid transparent; background-clip: content-box; }
    .paie-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; background-clip: content-box; border: 2px solid transparent; }

    @media (prefers-reduced-motion: reduce) {
      .paie-fade-up, .paie-fade, .paie-pop, .paie-slide, .paie-modal { animation: none; }
      .paie-shine::before { transition: none; }
    }
  `}</style>
);

// Onglets à indicateur glissant, compteurs, pastille « à compléter » et navigation clavier (← →)
const SlidingTabs = ({ tabs, active, onChange, label }) => {
  const listRef = useRef(null);
  const index = Math.max(0, tabs.findIndex((t) => t.id === active));

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next].id);
    listRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="relative grid rounded-xl bg-slate-100 p-1 ring-1 ring-inset ring-slate-200/70"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      <span
        aria-hidden="true"
        className="absolute bottom-1 left-1 top-1 rounded-lg bg-white shadow-md shadow-slate-900/10 ring-1 ring-slate-900/5 transition-transform duration-300 ease-out"
        style={{ width: `calc((100% - 0.5rem) / ${tabs.length})`, transform: `translateX(${index * 100}%)` }}
      />
      {tabs.map((t) => {
        const Icon = t.icon;
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={`relative z-10 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
              selected ? 'text-nuit' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="truncate">{t.label}</span>
            {t.badge !== undefined && (
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums transition-colors duration-200 ${
                  selected ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'
                }`}
              >
                {t.badge}
              </span>
            )}
            {t.dot && (
              <span className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" title="À renseigner" />
            )}
          </button>
        );
      })}
    </div>
  );
};

// Parcours en 4 étapes : chaque étape reflète l'état réel de la sélection
const Etapes = ({ etapes }) => (
  <ol className="grid grid-cols-2 gap-y-6 md:grid-cols-4">
    {etapes.map((etape, i) => {
      const Icon = etape.icon;
      const courant = !etape.ok && etapes.slice(0, i).every((x) => x.ok);
      const traitFait = etapes.slice(0, i + 1).every((x) => x.ok);
      return (
        <li key={etape.id} className="relative flex flex-col items-center px-2 text-center">
          {i < etapes.length - 1 && (
            <span
              aria-hidden="true"
              className="absolute left-[calc(50%+28px)] right-[calc(-50%+28px)] top-[18px] hidden h-1 overflow-hidden rounded-full bg-slate-200 md:block"
            >
              <span
                className="block h-full bg-gradient-to-r from-blue-500 to-indigo-500"
                style={{ width: traitFait ? '100%' : '0%', transition: 'width 600ms cubic-bezier(.2,.8,.2,1)' }}
              />
            </span>
          )}
          <button
            type="button"
            onClick={etape.onClick}
            disabled={!etape.onClick}
            className="group flex max-w-full flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-default"
          >
            <span
              className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full ring-4 ring-white transition-all duration-300 ${
                etape.encours
                  ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg'
                  : etape.ok
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg'
                    : courant
                      ? 'border-2 border-blue-500 bg-white text-blue-600'
                      : 'border-2 border-slate-200 bg-white text-slate-400'
              }`}
            >
              {etape.encours ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
              ) : etape.ok ? (
                <Check key="ok" className="paie-pop h-5 w-5" />
              ) : (
                <Icon className="h-5 w-5" />
              )}
              {courant && !etape.ok && (
                <span className="absolute inset-0 rounded-full border-2 border-blue-400 opacity-60 motion-safe:animate-ping" />
              )}
            </span>
            <span className={`mt-2 text-sm font-semibold ${etape.ok || courant ? 'text-nuit' : 'text-slate-400'}`}>{etape.label}</span>
            <span className="mt-0.5 max-w-[11rem] truncate text-xs text-slate-500">{etape.valeur}</span>
          </button>
        </li>
      );
    })}
  </ol>
);

// Clé de regroupement d'une période : le CODE de la filiale (repli sur l'id si le code est absent)
const cleFiliale = (paie) => {
  const code = normaliserCode(paie.filiale_code);
  if (code) return `code:${code}`;
  return paie.filiale_id != null ? `id:${paie.filiale_id}` : 'none';
};

// ═══════════════════════════════════════════════════════════════════════
//  CONSTRUCTEUR DE RAPPORT PERSONNALISÉ
//  L'utilisateur coche des « informations » (jamais de tables ni de SQL),
//  les renomme, les réordonne et voit l'aperçu. Les liens entre les tables
//  (agent ↔ paie du mois ↔ département…) sont déduits des champs choisis.
//  ⚠ Le serveur doit valider chaque `champ` reçu contre sa propre liste blanche.
// ═══════════════════════════════════════════════════════════════════════

const EXEMPLES = {
  montant: ['350 000', '275 000', '420 000'],
  heures: ['173,33', '160', '168'],
  pct: ['100 %', '92 %', '96 %'],
  entier: ['2', '0', '5'],
  date: ['12/03/2019', '01/09/2021', '15/01/2023'],
  texte: ['—', '—', '—'],
};

// champ(table, colonne, libellé affiché, type, exemples) -> { key: 'table.colonne', … }
const champ = (source, colonne, label, type = 'montant', ex) => ({
  key: `${source}.${colonne}`,
  source,
  label,
  type,
  ex: ex || EXEMPLES[type],
});

// Noms parlants des sources de données (affichés dans « Données reliées automatiquement »)
const SOURCES_LABEL = {
  personnel: 'Fiche agent',
  departement: 'Départements',
  titre: 'Fonctions',
  grade: 'Grades',
  type_personnel: 'Types de personnel',
  personnel_contrat: 'Contrats',
  type_contrat: 'Contrats',
  personnel_infopaie: 'Infos de paiement',
  banque: 'Banques',
  paie_element: 'Paie du mois',
  paie: 'Périodes de paie',
  filiale: 'Filiales',
  marques: 'Marques',
  business_unit: 'Business units',
  grille_personnel: 'Grilles salariales',
  motif_sortie: 'Motifs de sortie',
  campagne: 'Campagnes',
  manager: 'Managers',
  manager_n2: 'Managers',
};

// Charges propres à chaque filiale (colonnes réelles de la base)
const CHARGES_BUILDER = {
  BJ: {
    patronales: [['CNSS patronale', 'cnss_patronale'], ['VPS', 'vps'], ['Caisse sociale', 'caisse_sociale']],
    salariales: [['CNSS', 'cnss'], ['IPTS brut', 'ipts_brut'], ['Abattement', 'abattement'], ['IPTS net', 'iptsnet'], ['Redevance ORTB', 'redevance_ortb']],
  },
  CI: {
    patronales: [['CNPS patronale', 'cnps_patronale'], ['CMU patronale', 'cmu_patronal'], ['Accident de travail', 'accident_travail'], ['Prestations familiales', 'prestation_familiale'], ["FDFP taxe d'apprentissage", 'fdfp'], ['FDFP formation professionnelle continue', 'fdfp_continu']],
    salariales: [['CNPS', 'cnps'], ['CMU', 'cmu_employe'], ['Impôt sur salaire', 'impot_salaire'], ['Contribution nationale', 'contribution_nationale'], ['IGR', 'igr'], ['Impôt brut avant RICF', 'Impot_brut_avant_RICF'], ['Charges de famille (RITS)', 'Charges_Famille_RICF'], ['Total RITS', 'Total_Impot']],
  },
  GN: {
    patronales: [['CNSS patronale', 'cnss_patronale'], ['Caisse sociale', 'caisse_sociale']],
    salariales: [['CNSS', 'cnss'], ['Caisse sociale', 'caisse_sociale']],
  },
  GNB: {
    patronales: [['CNSS patronale', 'cnss_patronale'], ['VPS', 'vps'], ['Caisse sociale', 'caisse_sociale']],
    salariales: [['CNSS', 'cnss'], ['Caisse sociale', 'caisse_sociale']],
  },
  CG: {
    patronales: [['CNSS patronale', 'cnss_patronale'], ['VPS', 'vps'], ['Caisse sociale', 'caisse_sociale']],
    salariales: [['CNSS', 'cnss'], ['ITS brut', 'ipts_brut'], ['Abattement', 'abattement'], ['ITS net', 'iptsnet'], ['Redevance ORTB', 'redevance_ortb']],
  },
  CM: {
    patronales: [['Taxe communale', 'taxe_communale'], ['Redevance AV', 'redevence_AV'], ['CNPS Pension Vieillesse', 'cnps_patronale'], ['Crédit foncier patronal', 'credit_foncier_patron'], ['FNE', 'FNE'], ['Allocation familiale', 'allocation_familiale']],
    salariales: [['CNPS salariale', 'cnss'], ['Crédit foncier salarié', 'credit_foncier'], ['Accident de travail', 'accident_travail'], ['Caisse sociale', 'caisse_sociale']],
  },
};
// Hoope est une filiale ivoirienne : mêmes charges que la Côte d'Ivoire
const ALIAS_CHARGES = { CI_HOOPE: 'CI' };

const FAMILLE_STYLE = {
  identite: 'from-blue-500 to-indigo-600',
  emploi: 'from-violet-500 to-purple-600',
  temps: 'from-sky-500 to-cyan-600',
  salaire: 'from-emerald-500 to-teal-600',
  avances: 'from-amber-500 to-orange-600',
  retenues: 'from-rose-500 to-red-600',
  patronales: 'from-fuchsia-500 to-pink-600',
  banque: 'from-slate-500 to-slate-700',
  periode: 'from-indigo-500 to-blue-700',
  contact: 'from-cyan-500 to-blue-600',
  pieces: 'from-stone-500 to-stone-700',
  parcours: 'from-lime-500 to-green-600',
  organisation: 'from-orange-500 to-red-500',
};

// Union des charges de toutes les filiales (quand la filiale n'est pas précisée ou pas configurée)
const unionCharges = (clef) => {
  const vus = new Map();
  Object.values(CHARGES_BUILDER).forEach((c) => {
    c[clef].forEach(([label, key]) => {
      if (!vus.has(key)) vus.set(key, label);
    });
  });
  return Array.from(vus, ([key, label]) => champ('paie_element', key, label));
};

const construireFamilles = (codeFiliale, consolide) => {
  const cfg = consolide ? null : CHARGES_BUILDER[ALIAS_CHARGES[codeFiliale] || codeFiliale];
  const depuisConfig = (liste) => liste.map(([label, key]) => champ('paie_element', key, label));
  const noteCharges = cfg ? undefined : 'Les charges varient selon la filiale : une colonne reste vide si elle ne s\'applique pas.';
  return [
    {
      id: 'identite', label: 'Identité', icon: User, desc: "Qui est l'agent",
      champs: [
        champ('personnel', 'matricule', 'Matricule', 'texte', ['MCB-0142', 'MCB-0207', 'MCB-0318']),
        champ('personnel', 'nom', 'Nom', 'texte', ['DIALLO', 'BAH', 'KONÉ']),
        champ('personnel', 'prenom', 'Prénom', 'texte', ['Mamadou', 'Aïssatou', 'Ibrahim']),
        champ('personnel', 'autres_prenom', 'Autres prénoms', 'texte', ['Bakary', '—', 'Salif']),
        champ('personnel', 'sexe', 'Sexe', 'texte', ['M', 'F', 'M']),
        champ('personnel', 'date_naissance', 'Date de naissance', 'date'),
        champ('personnel', 'nationalite', 'Nationalité', 'texte', ['Guinéenne', 'Ivoirienne', 'Béninoise']),
        champ('personnel', 'situation_matrimoniale', 'Situation matrimoniale', 'texte', ['Marié(e)', 'Célibataire', 'Marié(e)']),
        champ('personnel', 'nombre_enfant_charge', 'Enfants à charge', 'entier'),
      ],
    },
    {
      id: 'contact', label: 'Contact & adresse', icon: Phone, desc: "Pour joindre l'agent",
      champs: [
        champ('personnel', 'numtel', 'Téléphone', 'texte', ['+224 620 00 00 01', '+225 07 00 00 02', '+229 97 00 00 03']),
        champ('personnel', 'adressemail', 'E-mail', 'texte', ['m.diallo@exemple.com', 'a.bah@exemple.com', 'i.kone@exemple.com']),
        champ('personnel', 'adresse_complete', 'Adresse', 'texte', ['Conakry, Kaloum', 'Abidjan, Cocody', 'Cotonou, Akpakpa']),
        champ('personnel', 'personne_contact', 'Personne à contacter', 'texte', ['Fatou Diallo', 'Awa Koné', 'Jean Adjovi']),
      ],
    },
    {
      id: 'pieces', label: "Pièces d'identité & permis", icon: FileText, desc: 'Pièce, permis de conduire, identifiant fiscal',
      champs: [
        champ('personnel', 'type_piece_id', "Type de pièce d'identité", 'texte', ['CNI', 'Passeport', 'CNI']),
        champ('personnel', 'num_piece_id', "N° de pièce d'identité", 'texte', ['P123456', 'P654321', 'P246810']),
        champ('personnel', 'lieu_delivrance_piece', 'Lieu de délivrance de la pièce', 'texte', ['Conakry', 'Abidjan', 'Cotonou']),
        champ('personnel', 'date_expiration_piece', 'Expiration de la pièce', 'date'),
        champ('personnel', 'has_permis_conduire', 'Permis de conduire', 'texte', ['Oui', 'Non', 'Oui']),
        champ('personnel', 'cat_permis_conduire', 'Catégorie de permis', 'texte', ['B', '—', 'C']),
        champ('personnel', 'numero_ifu', 'N° IFU (identifiant fiscal)', 'texte', ['IFU0012', 'IFU0034', 'IFU0056']),
      ],
    },
    {
      id: 'parcours', label: 'Formation & parcours', icon: GraduationCap, desc: 'Études et évolution de carrière',
      champs: [
        champ('personnel', 'domaine_etude', "Domaine d'étude", 'texte', ['Comptabilité', 'Droit', 'Informatique']),
        champ('personnel', 'niveau_etude', "Niveau d'étude", 'texte', ['Bac+5', 'Bac+3', 'Bac+2']),
        champ('personnel', 'date_derniere_promo', 'Dernière promotion', 'date'),
      ],
    },
    {
      id: 'emploi', label: 'Emploi', icon: Briefcase, desc: 'Poste, contrat et situation',
      champs: [
        champ('filiale', 'libelle', 'Filiale', 'texte', ['MCB RH Guinée', 'MCB RH Guinée', 'MCB RH Guinée']),
        champ('departement', 'libelle_departement', 'Département', 'texte', ['Finance', 'Logistique', 'Commercial']),
        champ('titre', 'nom', 'Fonction', 'texte', ['Comptable', 'Chauffeur', 'Conseiller']),
        champ('titre', 'nom_developpe', 'Fonction (intitulé complet)', 'texte', ['Comptable principal', 'Chauffeur de direction', 'Conseiller clientèle']),
        champ('grade', 'libelle', 'Grade', 'texte', ['G3', 'G1', 'G2']),
        champ('grade', 'code', 'Code du grade', 'texte', ['G03', 'G01', 'G02']),
        champ('grade', 'niveau', 'Niveau du grade', 'entier', ['3', '1', '2']),
        champ('type_personnel', 'libelle', 'Type de personnel', 'texte', ['Agents', 'Cadres', 'Agents']),
        champ('type_personnel', 'code', 'Code du type de personnel', 'texte', ['AG', 'CA', 'AG']),
        champ('type_contrat', 'nom', 'Type de contrat', 'texte', ['CDI', 'CDD', 'CDI']),
        champ('personnel', 'date_entree', "Date d'entrée", 'date'),
        champ('personnel', 'date_entree_contrat', "Date d'entrée du contrat", 'date'),
        champ('personnel_contrat', 'date_fin_contrat', 'Fin de contrat', 'date', ['—', '31/12/2026', '—']),
        champ('personnel', 'date_sortie', 'Date de sortie', 'date', ['—', '—', '30/06/2026']),
        champ('motif_sortie', 'libelle_motif', 'Motif de sortie', 'texte', ['—', '—', 'Démission']),
        champ('personnel', 'active', 'Situation (actif / inactif)', 'texte', ['Actif', 'Actif', 'Inactif']),
        champ('personnel', 'statut', 'Statut RH', 'texte', ['Confirmé', 'Période d\'essai', 'Confirmé']),
        champ('personnel', 'direct_indirect', 'Personnel direct / indirect', 'texte', ['Direct', 'Indirect', 'Direct']),
        champ('personnel', 'solde_conge', 'Solde de congés (jours)', 'entier', ['12', '3', '5']),
        champ('personnel', 'emplacement', 'Emplacement', 'texte', ['Siège', 'Agence Nord', 'Siège']),
        champ('personnel', 'numero_cnss', 'N° de sécurité sociale', 'texte', ['1234567', '7654321', '2468135']),
        champ('personnel', 'part_igr', "Parts d'IGR", 'entier', ['2', '1', '3']),
      ],
    },
    {
      id: 'organisation', label: 'Organisation & rattachement', icon: Building2, desc: 'Marque, business unit, manager, grille salariale',
      champs: [
        champ('marques', 'libelle', 'Marque', 'texte', ['Total', 'Shell', 'Total']),
        champ('marques', 'code', 'Code marque', 'texte', ['TOT', 'SHL', 'TOT']),
        champ('business_unit', 'libelle', 'Business unit', 'texte', ['BU Finance', 'BU Retail', 'BU Finance']),
        champ('business_unit', 'code', 'Code business unit', 'texte', ['BUF', 'BUR', 'BUF']),
        champ('campagne', 'Libelle_campagne', 'Campagne', 'texte', ['Campagne 2026', 'Campagne 2026', '—']),
        champ('personnel', 'departement_affectation', "Département d'affectation", 'texte', ['Finance', 'Logistique', 'Commercial']),
        champ('personnel', 'ville_affectation', "Ville d'affectation", 'texte', ['Conakry', 'Abidjan', 'Cotonou']),
        champ('personnel', 'agence_affectation', "Agence d'affectation", 'texte', ['Kaloum', 'Plateau', 'Cadjehoun']),
        champ('manager', 'matricule', 'Manager : matricule', 'texte', ['MCB-0010', 'MCB-0010', 'MCB-0022']),
        champ('manager', 'nom', 'Manager : nom', 'texte', ['BAH', 'BAH', 'SYLLA']),
        champ('manager', 'prenom', 'Manager : prénom', 'texte', ['Aïssatou', 'Aïssatou', 'Moussa']),
        champ('personnel', 'fonction_manager', 'Manager : fonction', 'texte', ['DAF', 'DAF', 'DRH']),
        champ('manager_n2', 'matricule', 'N+2 : matricule', 'texte', ['MCB-0003', 'MCB-0003', 'MCB-0003']),
        champ('manager_n2', 'nom', 'N+2 : nom', 'texte', ['CAMARA', 'CAMARA', 'CAMARA']),
        champ('manager_n2', 'prenom', 'N+2 : prénom', 'texte', ['Ibrahim', 'Ibrahim', 'Ibrahim']),
        champ('grille_personnel', 'Libelle_categorie', 'Catégorie (grille salariale)', 'texte', ['Cadre 1', 'Agent de maîtrise', 'Cadre 1']),
        champ('grille_personnel', 'salaire_base', 'Salaire de base de la grille'),
        champ('grille_personnel', 'sursalaire', 'Sursalaire de la grille'),
      ],
    },
    {
      id: 'temps', label: 'Temps de travail', icon: Clock, desc: 'Heures, absences et congés',
      champs: [
        champ('paie_element', 'heure_planifiee', 'Heures planifiées', 'heures'),
        champ('paie_element', 'heure_presence', 'Heures de présence', 'heures'),
        champ('paie_element', 'heure_feriee', 'Heures fériées', 'heures', ['8', '8', '8']),
        champ('paie_element', 'heure_absence_non_justifiee', 'Absences non justifiées (h)', 'heures', ['0', '4', '0']),
        champ('paie_element', 'heure_absence_non_justifiee_without_sanction', 'Absences pour sanction (h)', 'heures', ['0', '0', '2']),
        champ('paie_element', 'heure_absence_maladie', 'Permission / absence justifiée (h)', 'heures', ['0', '8', '0']),
        champ('paie_element', 'heure_payer', 'Heures à payer', 'heures'),
        champ('paie_element', 'taux_presence', 'Taux de présence', 'pct'),
        champ('paie_element', 'nombre_jour_pris_conge_annuel', 'Congés annuels pris (jours)', 'entier'),
        champ('paie_element', 'heure_pris_conge_speciaux', 'Congés spéciaux (h)', 'heures', ['0', '16', '0']),
        champ('paie_element', 'heure_conge_matrenite', 'Congé maternité (h)', 'heures', ['0', '0', '0']),
      ],
    },
    {
      id: 'salaire', label: 'Salaire & primes', icon: Wallet, desc: 'Du salaire de base au net à payer',
      champs: [
        champ('paie_element', 'salaire_base_courant', 'Salaire de base'),
        champ('paie_element', 'salaire_fixe', 'Salaire fixe'),
        champ('paie_element', 'prime_variable', 'Prime variable'),
        champ('paie_element', 'prime_anciennete', "Prime d'ancienneté"),
        champ('paie_element', 'prime_astreinte', "Prime d'astreinte"),
        champ('paie_element', 'prime_caisse', 'Prime de caisse'),
        champ('paie_element', 'prime_responsabilite', 'Prime de responsabilité'),
        champ('paie_element', 'prime_de_deplacement', 'Prime de déplacement'),
        champ('paie_element', 'prime_de_perdiem', 'Prime de perdiem'),
        champ('paie_element', 'prime_autre', 'Autre prime'),
        champ('paie_element', 'prime_gratification', 'Gratification'),
        champ('paie_element', 'remuneration_jour_ferie', 'Rémunération des jours fériés'),
        champ('paie_element', 'allocation_conge_annuel', 'Allocation congés annuels'),
        champ('paie_element', 'allocation_conge_speciaux', 'Allocation congés spéciaux'),
        champ('paie_element', 'montant_conge_matrenite', 'Montant congé maternité'),
        champ('paie_element', 'salaire_brut', 'Salaire brut'),
        champ('paie_element', 'salaire_brut_imposable', 'Salaire brut imposable'),
        champ('paie_element', 'salaire_net', 'Net à payer'),
      ],
    },
    {
      id: 'avances', label: 'Avances & régularisations', icon: Receipt, desc: 'Ce qui vient en déduction ou en rattrapage',
      champs: [
        champ('paie_element', 'montant_avance', 'Avance'),
        champ('paie_element', 'montant_retenu', 'Retenue'),
        champ('paie_element', 'montant_regularisation', 'Trop / moins perçu'),
      ],
    },
    {
      id: 'retenues', label: 'Retenues salariales', icon: Receipt, desc: "Cotisations et impôts à la charge de l'agent",
      note: noteCharges, specifique: Boolean(cfg),
      champs: cfg ? depuisConfig(cfg.salariales) : unionCharges('salariales'),
    },
    {
      id: 'patronales', label: 'Charges patronales', icon: Landmark, desc: "Cotisations à la charge de l'employeur",
      note: noteCharges, specifique: Boolean(cfg),
      champs: cfg ? depuisConfig(cfg.patronales) : unionCharges('patronales'),
    },
    {
      id: 'banque', label: 'Banque & paiement', icon: CreditCard, desc: 'Comment et où l\'agent est payé',
      champs: [
        champ('personnel_infopaie', 'type_paiement', 'Mode de paiement', 'texte', ['Virement', 'Virement', 'Espèces']),
        champ('banque', 'libelle_banque', 'Banque', 'texte', ['Ecobank', 'BICICI', 'NSIA']),
        champ('banque', 'code', 'Code banque', 'texte', ['ECO', 'BIC', 'NSI']),
        champ('personnel_infopaie', 'code', 'Code (infos de paiement)', 'texte', ['VIR01', 'VIR01', 'ESP02']),
        champ('personnel_infopaie', 'numero_compte', 'Numéro de compte', 'texte', ['0012 3456 7890', '0098 7654 3210', '0045 6789 0123']),
        champ('personnel', 'titulaire', 'Titulaire du compte', 'texte', ['DIALLO Mamadou', 'BAH Aïssatou', 'KONÉ Ibrahim']),
        champ('personnel', 'domiciliation', 'Domiciliation', 'texte', ['Ecobank Kaloum', 'BICICI Plateau', 'NSIA Cotonou']),
      ],
    },
    {
      id: 'periode', label: 'Période de paie', icon: Calendar, desc: 'La période concernée par le rapport',
      champs: [
        champ('paie', 'libelle_paie', 'Période', 'texte', ['Octobre 2026', 'Octobre 2026', 'Octobre 2026']),
        champ('paie', 'date_debut', 'Début de période', 'date', ['01/10/2026', '01/10/2026', '01/10/2026']),
        champ('paie', 'date_fin', 'Fin de période', 'date', ['31/10/2026', '31/10/2026', '31/10/2026']),
      ],
    },
  ];
};

// Modèles prêts à l'emploi : un clic remplit le rapport
const MODELES_BASE = [
  { id: 'personnel', label: 'Liste du personnel', icon: Users, champs: ['personnel.matricule', 'personnel.nom', 'personnel.prenom', 'departement.libelle_departement', 'titre.nom', 'personnel.date_entree'] },
  { id: 'salaires', label: 'Salaires nets', icon: Wallet, champs: ['personnel.matricule', 'personnel.nom', 'personnel.prenom', 'paie_element.salaire_brut', 'paie_element.salaire_net'] },
  { id: 'heures', label: 'Heures & absences', icon: Clock, champs: ['personnel.matricule', 'personnel.nom', 'paie_element.heure_planifiee', 'paie_element.heure_presence', 'paie_element.heure_absence_non_justifiee', 'paie_element.heure_absence_maladie', 'paie_element.taux_presence'] },
  { id: 'primes', label: 'Primes détaillées', icon: Gift, champs: ['personnel.matricule', 'personnel.nom', 'paie_element.prime_variable', 'paie_element.prime_anciennete', 'paie_element.prime_astreinte', 'paie_element.prime_caisse', 'paie_element.prime_responsabilite', 'paie_element.prime_de_deplacement', 'paie_element.prime_gratification'] },
  { id: 'organisation', label: 'Organisation', icon: Building2, champs: ['personnel.matricule', 'personnel.nom', 'personnel.prenom', 'marques.libelle', 'business_unit.libelle', 'departement.libelle_departement', 'manager.nom', 'manager.prenom'] },
  { id: 'virements', label: 'Virements bancaires', icon: CreditCard, champs: ['personnel.nom', 'personnel.prenom', 'banque.libelle_banque', 'personnel_infopaie.numero_compte', 'paie_element.salaire_net'] },
];

const normaliserTexte = (s) =>
  String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();


// ═══════════════════════════════════════════════════════════════════════
//  CALCULS PERSONNALISÉS — moteur d'évaluation (identique à celui du serveur)
//  Une formule = suite de jetons : colonne · nombre · opération · parenthèse · condition
// ═══════════════════════════════════════════════════════════════════════

const NUMERIQUES = ['montant', 'heures', 'pct', 'entier'];
const SYMBOLES = { '+': '+', '-': '−', '*': '×', '/': '÷' };
const COMPARATEURS = { '==': '=', '!=': '≠', '<': '<', '>': '>', '<=': '≤', '>=': '≥' };
const OPERATEURS_LOGIQUES = { '&&': 'ET', '||': 'OU' };

const evaluerFormule = (jetons, valeur) => {
  let i = 0;
  const lire = () => jetons[i++];
  const regarder = () => jetons[i];
  const est = (j, t, v) => !!j && j.t === t && (v === undefined || j.v === v);
  const erreur = (message) => { throw new Error(message); };

  const expression = (arrets = []) => {
    let v = terme(arrets);
    while (est(regarder(), 'op') && ['+', '-'].includes(regarder().v) && !arrets.includes(regarder().v)) {
      const op = lire().v;
      const d = terme(arrets);
      v = v === null || d === null ? null : op === '+' ? v + d : v - d;
    }
    return v;
  };

  const terme = (arrets = []) => {
    let v = facteur(arrets);
    while (est(regarder(), 'op') && ['*', '/'].includes(regarder().v) && !arrets.includes(regarder().v)) {
      const op = lire().v;
      const d = facteur(arrets);
      if (v === null || d === null) v = null;
      else if (op === '*') v *= d;
      else v = d === 0 ? null : v / d;
    }
    return v;
  };

  const comparaison = (arrets = []) => {
    let v = expression([...arrets, '__comparaison__']);
    if (est(regarder(), 'comp')) {
      const op = lire().v;
      const d = expression(arrets);
      if (v === null || d === null) return null;
      switch (op) {
        case '==': return v === d;
        case '!=': return v !== d;
        case '<': return v < d;
        case '>': return v > d;
        case '<=': return v <= d;
        case '>=': return v >= d;
        default: return false;
      }
    }
    return v;
  };

  const logique = (arrets = []) => {
    let v = comparaison(arrets);
    while (est(regarder(), 'oplog') && !arrets.includes(regarder().v)) {
      const op = lire().v;
      const d = comparaison(arrets);
      if (v === null || d === null) v = null;
      else v = op === '&&' ? Boolean(v && d) : Boolean(v || d);
    }
    return v;
  };

  const facteur = (arrets = []) => {
    const j = regarder();
    if (!j || (j.t === 'motcle' && arrets.includes(j.v))) erreur('Formule incomplète');
    lire();
    if (j.t === 'num') return Number(j.v);
    if (j.t === 'col') return valeur(j.champ);
    if (j.t === 'datecalc') {
      const lireDate = (champ, dateSaisie) => {
        const brut = dateSaisie || valeur(champ, true);
        if (brut === null || brut === undefined || brut === '') return null;
        if (brut instanceof Date) return Number.isNaN(brut.getTime()) ? null : brut;
        const texte = String(brut).trim();
        let date;
        const fr = texte.match(/^(\\d{1,2})[\\/.-](\\d{1,2})[\\/.-](\\d{4})$/);
        if (fr) date = new Date(Number(fr[3]), Number(fr[2]) - 1, Number(fr[1]));
        else date = new Date(texte);
        return Number.isNaN(date.getTime()) ? null : date;
      };
      if (j.op === 'difference-dates' || j.op === 'ajouter-jours') {
        const a = lireDate(j.a, j.dateA);
        if (!a) return null;
        if (j.op === 'ajouter-jours') {
          const jours = Number(j.nombre ?? 0);
          if (!Number.isFinite(jours)) return null;
          const resultat = new Date(a);
          resultat.setDate(resultat.getDate() + jours);
          return resultat.toISOString().slice(0, 10);
        }
        const b = lireDate(j.b, j.dateB);
        if (!b) return null;
        const jours = Math.abs(b.getTime() - a.getTime()) / 86400000;
        if (j.unite === 'semaines') return Math.round((jours / 7) * 100) / 100;
        if (j.unite === 'mois') return Math.round((jours / 30.4375) * 100) / 100;
        if (j.unite === 'annees') return Math.round((jours / 365.2425) * 100) / 100;
        return Math.round(jours * 100) / 100;
      }
      const a = Number(valeur(j.a));
      if (!Number.isFinite(a)) return null;
      if (j.op === 'heures-en-minutes') return Math.round(a * 60 * 100) / 100;
      if (j.op === 'minutes-en-heures') return Math.round((a / 60) * 100) / 100;
      if (j.op === 'difference-heures' || j.op === 'addition-heures') {
        const b = Number(valeur(j.b));
        if (!Number.isFinite(b)) return null;
        return Math.round((j.op === 'difference-heures' ? a - b : a + b) * 100) / 100;
      }
      return null;
    }
    if (j.t === 'par' && j.v === '(') {
      const v = expression();
      const f = lire();
      if (!est(f, 'par', ')')) erreur('Parenthèse fermante manquante');
      return v;
    }
    if (j.t === 'cond' && j.v === 'IF') {
      const condition = logique(['ALORS']);
      if (!est(lire(), 'motcle', 'ALORS')) erreur('Condition : cliquez sur « ALORS » après la règle.');
      const valAlors = expression(['SINON']);
      if (!est(lire(), 'motcle', 'SINON')) erreur('Condition : précisez le résultat « SINON ».');
      const valSinon = expression(arrets);
      return condition ? valAlors : valSinon;
    }
    erreur('Formule invalide');
  };

  const resultat = expression();
  if (i !== jetons.length) throw new Error('Formule invalide');
  return resultat === null || (typeof resultat === 'number' && !Number.isFinite(resultat)) ? null : (typeof resultat === 'number' ? Math.round(resultat * 100) / 100 : resultat);
};

const MESSAGES_FORMULE = {
  'Formule incomplète': "La formule est incomplète : il manque une colonne ou un nombre (à la fin, ou après une opération).",
  'Parenthèse fermante manquante': "Une parenthèse ouverte n'est pas refermée.",
  'Formule invalide': "L'enchaînement n'est pas valide : deux éléments se suivent sans opération, ou une parenthèse est en trop.",
  'IF nécessite 3 parties': "Une condition doit comporter une règle, un résultat ALORS et un résultat SINON.",
};

const versNombre = (v) => Number(String(v).replace(',', '.'));
const normaliserJetons = (jetons) => jetons.map((j) => (j.t === 'num' ? { t: 'num', v: versNombre(j.v) } : j));

// Vérifie une formule et renvoie un message compréhensible par un non-spécialiste
const verifierFormule = (jetons) => {
  if (jetons.length === 0) return { ok: false, erreur: 'Ajoutez au moins une colonne ou un nombre.' };
  if (jetons.some((j) => j.t === 'col' && !j.champ)) return { ok: false, erreur: 'Choisissez une colonne dans chaque case bleue.' };
  if (jetons.some((j) => j.t === 'num' && (String(j.v).trim() === '' || !Number.isFinite(versNombre(j.v))))) {
    return { ok: false, erreur: 'Saisissez un nombre dans chaque case grise.' };
  }
  try {
    evaluerFormule(normaliserJetons(jetons), () => 1);
  } catch (e) {
    return { ok: false, erreur: MESSAGES_FORMULE[e.message] || e.message };
  }
  return { ok: true };
};

// « ( Salaire de base × 40 ) ÷ 100 » ou « SI ( salaire > 50000 ) ALORS salaire SINON 0 »
const formuleEnTexte = (jetons, libelleDe) =>
  jetons
    .map((j) => {
      if (j.t === 'col') return libelleDe(j.champ);
      if (j.t === 'op') return SYMBOLES[j.v];
      if (j.t === 'comp') return COMPARATEURS[j.v];
      if (j.t === 'oplog') return OPERATEURS_LOGIQUES[j.v];
      if (j.t === 'cond' && j.v === 'IF') return 'SI';
      if (j.t === 'datecalc') {
        const noms = { 'difference-dates': `Écart entre ${j.dateA ? j.dateA : libelleDe(j.a)} et ${j.dateB ? j.dateB : libelleDe(j.b)} (${j.unite || 'jours'})`, 'ajouter-jours': `Ajouter ${j.nombre || 0} jour(s) à ${j.dateA ? j.dateA : libelleDe(j.a)}`,  'heures-en-minutes': `Convertir ${libelleDe(j.a)} en minutes`, 'minutes-en-heures': `Convertir ${libelleDe(j.a)} en heures`, 'difference-heures': `${libelleDe(j.a)} − ${libelleDe(j.b)} (heures)`, 'addition-heures': `${libelleDe(j.a)} + ${libelleDe(j.b)} (heures)` };
        return noms[j.op] || 'Calcul date/heure';
      }
      if (j.t === 'motcle') return j.v;
      if (j.t === 'par') return j.v;
      return String(j.v);
    })
    .join(' ')
    .replace(/\( /g, '(')
    .replace(/ \)/g, ')')
    .replace(/SI (.*?) ALORS (.*?) SINON (.*?)$/, 'SI $1 ALORS $2 SINON $3');

const parseExemple = (texte) => {
  const n = Number(String(texte).replace(/[\s\u00a0\u202f%]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const nouvelIdCalcul = () => `calcul:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Modèles de départ pour construire un calcul sans rien connaître aux formules
const DEPARTS_CALCUL = [
  { id: 'somme', label: 'Somme', aide: 'A + B', jetons: () => [{ t: 'col', champ: '' }, { t: 'op', v: '+' }, { t: 'col', champ: '' }] },
  { id: 'difference', label: 'Différence', aide: 'A − B', jetons: () => [{ t: 'col', champ: '' }, { t: 'op', v: '-' }, { t: 'col', champ: '' }] },
  {
    id: 'pourcentage', label: 'Pourcentage', aide: '(A × 40) ÷ 100',
    jetons: () => [{ t: 'par', v: '(' }, { t: 'col', champ: '' }, { t: 'op', v: '*' }, { t: 'num', v: '40' }, { t: 'par', v: ')' }, { t: 'op', v: '/' }, { t: 'num', v: '100' }],
  },
  { id: 'multiplication', label: 'Multiplication', aide: 'A × B', jetons: () => [{ t: 'col', champ: '' }, { t: 'op', v: '*' }, { t: 'num', v: '' }] },
  { id: 'division', label: 'Division', aide: 'A ÷ B', jetons: () => [{ t: 'col', champ: '' }, { t: 'op', v: '/' }, { t: 'col', champ: '' }] },
];

// ═══════════════════════════════════════════════════════════════════════
//  FORMATS DE SORTIE : affichage, Excel (.xlsx) et PDF (impression)
// ═══════════════════════════════════════════════════════════════════════

const formaterValeur = (valeur, type) => {
  if (valeur === null || valeur === undefined || valeur === '') return '—';
  if (type === 'date') {
    const d = new Date(valeur);
    return Number.isNaN(d.getTime()) ? String(valeur) : d.toLocaleDateString('fr-FR');
  }
  if (typeof valeur === 'number') {
    const texte = valeur.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
    return type === 'pct' ? `${texte} %` : texte;
  }
  return String(valeur);
};
const aDroite = (type) => NUMERIQUES.includes(type);

const GROUPES_LIBELLES = {
  identite: 'Identité', emploi: 'Emploi', periode: 'Période', temps: 'Temps de travail', salaire: 'Rémunération',
  avances: 'Avances & régularisations', retenues: 'Retenues salariales', patronales: 'Charges patronales',
  banque: 'Paiement', calcul: 'Calculs', contact: 'Contact', pieces: 'Pièces & permis',
  parcours: 'Formation & parcours', organisation: 'Organisation',
};

const nomDeFichier = (resultat, extension) => {
  const propre = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return `${propre(resultat.titre) || 'rapport'}_${propre(resultat.periode?.libelle_paie) || 'periode'}.${extension}`;
};

const telechargerBlob = (blob, nom) => {
  const lien = document.createElement('a');
  lien.href = URL.createObjectURL(blob);
  lien.download = nom;
  lien.click();
  setTimeout(() => URL.revokeObjectURL(lien.href), 10000);
};

// ── Excel : vrai fichier .xlsx écrit à la main (archive ZIP sans compression), sans dépendance ──
const TABLE_CRC = (() => {
  const t = [];
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
const crc32 = (octets) => {
  let c = 0xffffffff;
  for (let i = 0; i < octets.length; i += 1) c = TABLE_CRC[(c ^ octets[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};

const creerZip = (fichiers) => {
  const encodeur = new TextEncoder();
  const morceaux = [];
  const centrale = [];
  let decalage = 0;
  fichiers.forEach(({ nom, contenu }) => {
    const nomOctets = encodeur.encode(nom);
    const donnees = encodeur.encode(contenu);
    const crc = crc32(donnees);
    const local = new Uint8Array(30 + nomOctets.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); lv.setUint16(4, 20, true); lv.setUint16(6, 0x0800, true); lv.setUint16(8, 0, true);
    lv.setUint16(10, 0, true); lv.setUint16(12, 0x21, true); lv.setUint32(14, crc, true);
    lv.setUint32(18, donnees.length, true); lv.setUint32(22, donnees.length, true);
    lv.setUint16(26, nomOctets.length, true); lv.setUint16(28, 0, true);
    local.set(nomOctets, 30);
    morceaux.push(local, donnees);
    const entree = new Uint8Array(46 + nomOctets.length);
    const ev = new DataView(entree.buffer);
    ev.setUint32(0, 0x02014b50, true); ev.setUint16(4, 20, true); ev.setUint16(6, 20, true); ev.setUint16(8, 0x0800, true);
    ev.setUint16(10, 0, true); ev.setUint16(12, 0, true); ev.setUint16(14, 0x21, true); ev.setUint32(16, crc, true);
    ev.setUint32(20, donnees.length, true); ev.setUint32(24, donnees.length, true); ev.setUint16(28, nomOctets.length, true);
    ev.setUint32(42, decalage, true);
    entree.set(nomOctets, 46);
    centrale.push(entree);
    decalage += local.length + donnees.length;
  });
  const tailleCentrale = centrale.reduce((s, e) => s + e.length, 0);
  const fin = new Uint8Array(22);
  const fv = new DataView(fin.buffer);
  fv.setUint32(0, 0x06054b50, true); fv.setUint16(8, fichiers.length, true); fv.setUint16(10, fichiers.length, true);
  fv.setUint32(12, tailleCentrale, true); fv.setUint32(16, decalage, true);
  return new Blob([...morceaux, ...centrale, fin], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

const echapperXml = (s) =>
  String(s ?? '').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const lettreColonne = (n) => {
  let s = '';
  for (let k = n + 1; k > 0; k = Math.floor((k - 1) / 26)) s = String.fromCharCode(65 + ((k - 1) % 26)) + s;
  return s;
};

const genererXlsx = (resultat) => {
  const { colonnes, lignes, totaux } = resultat;
  const texte = (ref, valeur, style = 0) => `<c r="${ref}" t="inlineStr"${style ? ` s="${style}"` : ''}><is><t xml:space="preserve">${echapperXml(valeur)}</t></is></c>`;
  const nombre = (ref, valeur, style = 2) => `<c r="${ref}" s="${style}"><v>${valeur}</v></c>`;

  const rangees = [];
  rangees.push(`<row r="1">${colonnes.map((c, i) => texte(`${lettreColonne(i)}1`, c.libelle, 1)).join('')}</row>`);
  lignes.forEach((ligne, r) => {
    const num = r + 2;
    const cellules = colonnes.map((c, i) => {
      const v = ligne[c.cle];
      const ref = `${lettreColonne(i)}${num}`;
      if (v === null || v === undefined || v === '') return '';
      if (typeof v === 'number') return nombre(ref, v);
      return texte(ref, c.type === 'date' ? formaterValeur(v, 'date') : v);
    });
    rangees.push(`<row r="${num}">${cellules.join('')}</row>`);
  });
  if (totaux) {
    const num = lignes.length + 2;
    const cellules = colonnes.map((c, i) => {
      const ref = `${lettreColonne(i)}${num}`;
      if (i === 0) return texte(ref, 'TOTAL', 4);
      return c.cle in totaux ? nombre(ref, totaux[c.cle], 3) : '';
    });
    rangees.push(`<row r="${num}">${cellules.join('')}</row>`);
  }

  const largeurs = colonnes.map((c, i) => {
    const longueur = Math.max(String(c.libelle).length, ...lignes.slice(0, 200).map((l) => String(formaterValeur(l[c.cle], c.type)).length));
    return `<col min="${i + 1}" max="${i + 1}" width="${Math.min(Math.max(longueur + 3, 12), 50)}" customWidth="1"/>`;
  });
  const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
  const entete = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
  const feuille = `${entete}<worksheet xmlns="${NS}"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><cols>${largeurs.join('')}</cols><sheetData>${rangees.join('')}</sheetData></worksheet>`;
  const styles = `${entete}<styleSheet xmlns="${NS}"><numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0.##"/></numFmts><fonts count="3"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF2563EB"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="5"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="164" fontId="2" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/><xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
  const nomFeuille = String(resultat.titre || 'Rapport').replace(/[\[\]:*?/\\]/g, ' ').trim().slice(0, 31) || 'Rapport';
  const classeur = `${entete}<workbook xmlns="${NS}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${echapperXml(nomFeuille)}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const REL = 'http://schemas.openxmlformats.org/package/2006/relationships';
  const OD = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
  return creerZip([
    { nom: '[Content_Types].xml', contenu: `${entete}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>` },
    { nom: '_rels/.rels', contenu: `${entete}<Relationships xmlns="${REL}"><Relationship Id="rId1" Type="${OD}/officeDocument" Target="xl/workbook.xml"/></Relationships>` },
    { nom: 'xl/workbook.xml', contenu: classeur },
    { nom: 'xl/_rels/workbook.xml.rels', contenu: `${entete}<Relationships xmlns="${REL}"><Relationship Id="rId1" Type="${OD}/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="${OD}/styles" Target="styles.xml"/></Relationships>` },
    { nom: 'xl/worksheets/sheet1.xml', contenu: feuille },
    { nom: 'xl/styles.xml', contenu: styles },
  ]);
};

// ── PDF : document mis en page puis imprimé (« Enregistrer au format PDF »), sans dépendance ──
const echapperHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const CSS_IMPRESSION = `
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; font-size: 10px; margin: 0; }
  h1 { font-size: 16px; margin: 0 0 2px; color: #1e3a8a; }
  .sous-titre { color: #6b7280; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #2563eb; color: #fff; text-align: left; padding: 5px 6px; font-size: 9px; text-transform: uppercase; }
  td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; }
  tr:nth-child(even) td { background: #f9fafb; }
  .d { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { background: #dbeafe; font-weight: bold; border-top: 2px solid #2563eb; }
  thead { display: table-header-group; } tr { page-break-inside: avoid; }
  .bulletin { page-break-after: always; border: 1px solid #d1d5db; border-radius: 6px; padding: 12px 16px; }
  .bulletin:last-child { page-break-after: auto; }
  .bulletin h2 { font-size: 14px; margin: 0 0 8px; color: #1e3a8a; }
  .grille { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 14px; }
  .groupe h3 { font-size: 9px; text-transform: uppercase; letter-spacing: .05em; color: #2563eb; border-bottom: 1px solid #bfdbfe; margin: 0 0 4px; padding-bottom: 2px; }
  .ligne { display: flex; justify-content: space-between; gap: 8px; padding: 1.5px 0; }
  .ligne span:first-child { color: #4b5563; }
  .net { margin-top: 10px; padding: 8px 12px; background: #1e3a8a; color: #fff; border-radius: 6px; display: flex; justify-content: space-between; font-size: 13px; font-weight: bold; }
`;

const htmlRapport = (resultat) => {
  const { colonnes, lignes, totaux, titre, periode, type_rapport: typeRapport } = resultat;
  const sousTitre = [periode?.libelle_paie, periode?.filiale_libelle].filter(Boolean).join(' · ');
  const en = `<h1>${echapperHtml(titre)}</h1><div class="sous-titre">${echapperHtml(sousTitre)} — ${lignes.length} ligne(s)</div>`;

  if (typeRapport === 'bulletin_paie') {
    const CLES_TOUJOURS = ['paie_element.salaire_brut', 'paie_element.salaire_net', 'paie_element.salaire_base_courant'];
    const MASQUER_ZERO = ['salaire', 'avances', 'retenues', 'patronales'];
    const groupes = [];
    colonnes.forEach((c) => {
      if (c.cle === 'paie_element.salaire_net') return;
      let g = groupes.find((x) => x.id === c.groupe);
      if (!g) { g = { id: c.groupe, colonnes: [] }; groupes.push(g); }
      g.colonnes.push(c);
    });
    const net = colonnes.find((c) => c.cle === 'paie_element.salaire_net');
    const corps = lignes.map((l) => {
      const nom = [l['personnel.nom'], l['personnel.prenom']].filter(Boolean).join(' ');
      const blocs = groupes.map((g) => {
        const visibles = g.colonnes.filter((c) => {
          const v = l[c.cle];
          if (v === null || v === undefined || v === '') return false;
          return !(MASQUER_ZERO.includes(g.id) && v === 0 && !CLES_TOUJOURS.includes(c.cle));
        });
        if (visibles.length === 0) return '';
        return `<div class="groupe"><h3>${echapperHtml(GROUPES_LIBELLES[g.id] || g.id)}</h3>${visibles
          .map((c) => `<div class="ligne"><span>${echapperHtml(c.libelle)}</span><span>${echapperHtml(formaterValeur(l[c.cle], c.type))}</span></div>`)
          .join('')}</div>`;
      }).join('');
      return `<section class="bulletin"><h2>Bulletin de paie — ${echapperHtml(nom)}</h2><div class="sous-titre">${echapperHtml(sousTitre)}</div><div class="grille">${blocs}</div>${net ? `<div class="net"><span>Net à payer</span><span>${echapperHtml(formaterValeur(l[net.cle], 'montant'))}</span></div>` : ''}</section>`;
    }).join('');
    return `<!doctype html><html><head><meta charset="utf-8"><title>${echapperHtml(titre)}</title><style>${CSS_IMPRESSION}</style></head><body>${corps}</body></html>`;
  }

  const tete = colonnes.map((c) => `<th class="${aDroite(c.type) ? 'd' : ''}">${echapperHtml(c.libelle)}</th>`).join('');
  const corps = lignes
    .map((l) => `<tr>${colonnes.map((c) => `<td class="${aDroite(c.type) ? 'd' : ''}">${echapperHtml(formaterValeur(l[c.cle], c.type))}</td>`).join('')}</tr>`)
    .join('');
  const pied = totaux
    ? `<tr class="total">${colonnes.map((c, i) => `<td class="${aDroite(c.type) ? 'd' : ''}">${i === 0 ? 'TOTAL' : c.cle in totaux ? echapperHtml(formaterValeur(totaux[c.cle], c.type)) : ''}</td>`).join('')}</tr>`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${echapperHtml(titre)}</title><style>${CSS_IMPRESSION}</style></head><body>${en}<table><thead><tr>${tete}</tr></thead><tbody>${corps}${pied}</tbody></table></body></html>`;
};

const imprimerHtml = (html) => {
  const cadre = document.createElement('iframe');
  cadre.setAttribute('aria-hidden', 'true');
  cadre.setAttribute('data-impression', 'rapport');
  cadre.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(cadre);
  const doc = cadre.contentDocument || cadre.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      cadre.contentWindow.focus();
      cadre.contentWindow.print();
    } catch (e) {
      console.error('Impression impossible:', e);
    }
  }, 250);
  setTimeout(() => cadre.parentNode && cadre.parentNode.removeChild(cadre), 60000);
};

const telechargerRapport = (format, resultat) => {
  if (format === 'excel') telechargerBlob(genererXlsx(resultat), nomDeFichier(resultat, 'xlsx'));
  else imprimerHtml(htmlRapport(resultat));
};

// ═══════════════════════════════════════════════════════════════════════
//  TABLEAU DE DONNÉES RÉELLES (aperçu et résultat)
// ═══════════════════════════════════════════════════════════════════════

const TableauDonnees = ({ colonnes, lignes, totaux, maxLignes = 10 }) => (
  <div className="paie-scroll overflow-x-auto">
    <table className="w-full border-separate border-spacing-0 text-sm">
      <thead>
        <tr>
          {colonnes.map((c) => (
            <th
              key={c.cle}
              className={`whitespace-nowrap border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 ${aDroite(c.type) ? 'text-right' : 'text-left'}`}
            >
              {c.libelle}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.slice(0, maxLignes).map((ligne, r) => (
          <tr key={r} className={`transition-colors hover:bg-blue-50/60 ${r % 2 ? 'bg-slate-50/50' : 'bg-white'}`}>
            {colonnes.map((c) => {
              const texte = formaterValeur(ligne[c.cle], c.type);
              return (
                <td
                  key={c.cle}
                  className={`whitespace-nowrap border-b border-slate-100 px-4 py-2.5 tabular-nums ${aDroite(c.type) ? 'text-right font-medium text-slate-800' : 'text-slate-700'} ${texte === '—' ? '!text-slate-300' : ''}`}
                >
                  {texte}
                </td>
              );
            })}
          </tr>
        ))}
        {totaux && (
          <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 font-bold text-nuit">
            {colonnes.map((c, i) => (
              <td key={c.cle} className={`whitespace-nowrap border-t-2 border-blue-200 px-4 py-3 tabular-nums ${aDroite(c.type) ? 'text-right' : ''}`}>
                {i === 0 ? 'TOTAL' : c.cle in totaux ? formaterValeur(totaux[c.cle], c.type) : ''}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════
//  ÉDITEUR DE CALCUL : on assemble une phrase (colonnes, nombres, opérations)
// ═══════════════════════════════════════════════════════════════════════

const BOUTON_OP = [['+', '+', 'plus'], ['-', '−', 'moins'], ['*', '×', 'multiplié par'], ['/', '÷', 'divisé par']];
const BOUTON_COMP = [['==', '=', 'égal à'], ['!=', '≠', 'différent de'], ['<', '<', 'inférieur à'], ['>', '>', 'supérieur à'], ['<=', '≤', 'inférieur ou égal à'], ['>=', '≥', 'supérieur ou égal à']];
const BOUTON_LOG = [['&&', 'ET', 'et'], ['||', 'OU', 'ou']];

const EditeurCalcul = ({ champsNumeriques, libelleDe, indexChamps, initial, onValider, onAnnuler }) => {
  const [nom, setNom] = useState(initial?.libelle || '');
  const [jetons, setJetons] = useState(
    initial?.formule ? initial.formule.map((j) => (j.t === 'num' ? { ...j, v: String(j.v) } : { ...j })) : []
  );
  const [champCondition, setChampCondition] = useState('');
  const [comparateurCondition, setComparateurCondition] = useState('>=');
  const [seuilCondition, setSeuilCondition] = useState('75000');
  const [typeValeurDroite, setTypeValeurDroite] = useState('fixe');
  const [champValeurDroite, setChampValeurDroite] = useState('');
  const [alorsExpression, setAlorsExpression] = useState({ type: 'pourcentage', champ: '', taux: '3', valeur: '0', aType: 'colonne', aChamp: '', aValeur: '', op: '*', bType: 'fixe', bChamp: '', bValeur: '0.03' });
  const [sinonExpression, setSinonExpression] = useState({ type: 'pourcentage', champ: '', taux: '3', valeur: '0', aType: 'colonne', aChamp: '', aValeur: '', op: '*', bType: 'fixe', bChamp: '', bValeur: '0.03' });
  const [modeAjoutCondition, setModeAjoutCondition] = useState('addition');
  const [outilTemps, setOutilTemps] = useState('difference-dates');
  const [champTempsA, setChampTempsA] = useState('');
  const [champTempsB, setChampTempsB] = useState('');
  const [modeDateA, setModeDateA] = useState('colonne');
  const [modeDateB, setModeDateB] = useState('colonne');
  const [dateSaisieA, setDateSaisieA] = useState('');
  const [dateSaisieB, setDateSaisieB] = useState('');
  const [uniteTemps, setUniteTemps] = useState('jours');
  const [nombreJours, setNombreJours] = useState('30');
  const champsDates = useMemo(() => Object.entries(indexChamps).filter(([, c]) => c.type === 'date' && c.ex?.length), [indexChamps]);
  const champsHeures = useMemo(() => Object.entries(indexChamps).filter(([, c]) => c.type === 'heures' && c.ex?.length), [indexChamps]);
  const champsTemps = outilTemps.includes('dates') || outilTemps === 'ajouter-jours' ? champsDates : champsHeures;
  const verification = verifierFormule(jetons);
  const maj = (i, patch) => setJetons(jetons.map((j, k) => (k === i ? { ...j, ...patch } : j)));
  const ajouter = (j) => setJetons([...jetons, j]);
  const retirer = (i) => setJetons(jetons.filter((_, k) => k !== i));
  const echantillon = [0, 1, 2].map((r) =>
    verification.ok ? evaluerFormule(normaliserJetons(jetons), (champ, brut = false) => brut ? indexChamps[champ]?.ex?.[r] ?? null : parseExemple(indexChamps[champ]?.ex?.[r]) ?? 0) : null
  );
  const connus = new Set(champsNumeriques.flatMap((g) => g.champs.map((c) => c.key)));
  const construireExpressionBranche = (cfg, champParDefaut) => {
    const operande = (type, champ, valeur) => type === 'colonne'
      ? [{ t: 'col', champ: champ || champParDefaut || '' }]
      : [{ t: 'num', v: versNombre(valeur ?? '0') }];
    if (cfg.type === 'colonne') return [{ t: 'col', champ: cfg.champ || champParDefaut || '' }];
    if (cfg.type === 'fixe') return [{ t: 'num', v: versNombre(cfg.valeur || '0') }];
    if (cfg.type === 'pourcentage') return [
      { t: 'col', champ: cfg.champ || champParDefaut || '' },
      { t: 'op', v: '*' },
      { t: 'num', v: versNombre(cfg.taux || '0') / 100 },
    ];
    return [
      ...operande(cfg.aType, cfg.aChamp, cfg.aValeur),
      { t: 'op', v: cfg.op || '*' },
      ...operande(cfg.bType, cfg.bChamp, cfg.bValeur),
    ];
  };
  const expressionValide = (cfg) => {
    if (cfg.type === 'colonne') return Boolean(cfg.champ || champCondition);
    if (cfg.type === 'fixe') return cfg.valeur !== '' && Number.isFinite(versNombre(cfg.valeur));
    if (cfg.type === 'pourcentage') return Boolean(cfg.champ || champCondition) && cfg.taux !== '' && Number.isFinite(versNombre(cfg.taux));
    const okA = cfg.aType === 'colonne' ? Boolean(cfg.aChamp || champCondition) : cfg.aValeur !== '' && Number.isFinite(versNombre(cfg.aValeur));
    const okB = cfg.bType === 'colonne' ? Boolean(cfg.bChamp || champCondition) : cfg.bValeur !== '' && Number.isFinite(versNombre(cfg.bValeur));
    return okA && okB;
  };
  const ajouterConditionGuidee = () => {
    if (!champCondition || seuilCondition === '' || (typeValeurDroite === 'fixe' && !Number.isFinite(versNombre(seuilCondition))) || (typeValeurDroite === 'colonne' && !champValeurDroite) || !expressionValide(alorsExpression) || !expressionValide(sinonExpression)) return;
    const bloc = [
      { t: 'par', v: '(' }, { t: 'cond', v: 'IF' }, { t: 'col', champ: champCondition },
      { t: 'comp', v: comparateurCondition },
      ...(typeValeurDroite === 'colonne' ? [{ t: 'col', champ: champValeurDroite }] : [{ t: 'num', v: versNombre(seuilCondition) }]),
      { t: 'motcle', v: 'ALORS' }, ...construireExpressionBranche(alorsExpression, champCondition),
      { t: 'motcle', v: 'SINON' }, ...construireExpressionBranche(sinonExpression, champCondition), { t: 'par', v: ')' },
    ];
    setJetons((prev) => {
      if (modeAjoutCondition === 'addition') return [...prev, ...(prev.length ? [{ t: 'op', v: '+' }] : []), ...bloc];
      let debut = -1;
      for (let k = prev.length - 2; k >= 0; k--) {
        if (prev[k]?.t === 'par' && prev[k].v === '(' && prev[k + 1]?.t === 'cond') { debut = k; break; }
      }
      if (debut < 0) return [...prev, ...(prev.length ? [{ t: 'op', v: '+' }] : []), ...bloc];
      let profondeurPar = 0, fin = -1;
      for (let k = debut; k < prev.length; k++) {
        if (prev[k].t === 'par' && prev[k].v === '(') profondeurPar++;
        if (prev[k].t === 'par' && prev[k].v === ')') { profondeurPar--; if (profondeurPar === 0) { fin = k; break; } }
      }
      if (fin < 0) return [...prev, ...(prev.length ? [{ t: 'op', v: '+' }] : []), ...bloc];
      let alorsIndex = -1, sinonIndex = -1, niveauCondition = 0;
      for (let k = debut + 2; k < fin; k++) {
        if (prev[k].t === 'cond') niveauCondition++;
        if (prev[k].t === 'motcle' && prev[k].v === 'ALORS' && niveauCondition === 0 && alorsIndex < 0) alorsIndex = k;
        if (prev[k].t === 'motcle' && prev[k].v === 'SINON') {
          if (niveauCondition === 0) { sinonIndex = k; break; }
          niveauCondition--;
        }
      }
      if (alorsIndex < 0 || sinonIndex < 0) return [...prev, ...(prev.length ? [{ t: 'op', v: '+' }] : []), ...bloc];
      const insertionDebut = modeAjoutCondition === 'dans-alors' ? alorsIndex + 1 : sinonIndex + 1;
      const insertionFin = modeAjoutCondition === 'dans-alors' ? sinonIndex : fin;
      return [...prev.slice(0, insertionDebut), ...bloc, ...prev.slice(insertionFin)];
    });
  };
  const ajouterCalculTemps = () => {
    const operationDate = outilTemps === 'difference-dates' || outilTemps === 'ajouter-jours';
    if (operationDate ? (modeDateA === 'saisie' ? !dateSaisieA : !champTempsA) : !champTempsA) return;
    if (outilTemps === 'difference-dates' && (modeDateB === 'saisie' ? !dateSaisieB : !champTempsB)) return;
    if (['difference-heures', 'addition-heures'].includes(outilTemps) && !champTempsB) return;
    const dateAProps = operationDate && modeDateA === 'saisie' ? { dateA: dateSaisieA } : {};
    const dateBProps = outilTemps === 'difference-dates' && modeDateB === 'saisie' ? { dateB: dateSaisieB } : {};
    const token = outilTemps === 'ajouter-jours'
      ? { t: 'datecalc', op: outilTemps, a: champTempsA, ...dateAProps, nombre: versNombre(nombreJours) }
      : ['heures-en-minutes', 'minutes-en-heures'].includes(outilTemps)
        ? { t: 'datecalc', op: outilTemps, a: champTempsA }
        : { t: 'datecalc', op: outilTemps, a: champTempsA, b: champTempsB, ...dateAProps, ...dateBProps, unite: uniteTemps };
    setJetons((prev) => token.op === 'ajouter-jours' ? [token] : [...prev, ...(prev.length ? [{ t: 'op', v: '+' }] : []), token]);
    if (!nom.trim()) setNom(({ 'difference-dates': 'Durée entre dates', 'ajouter-jours': 'Date calculée', 'heures-en-minutes': 'Minutes calculées', 'minutes-en-heures': 'Heures calculées', 'difference-heures': 'Écart d’heures', 'addition-heures': 'Total heures' })[outilTemps]);
  };
  const classePuce = 'inline-flex items-center gap-1 rounded-xl border bg-white p-1 shadow-sm';

  return (
    <section aria-label="Éditeur de calcul" className="paie-fade-up rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/70 to-indigo-50/50 p-5 shadow-lg shadow-blue-900/5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white shadow-lg">
            <Calculator className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{initial ? 'Modifier le calcul' : 'Ajouter un calcul'}</h3>
            <p className="text-xs text-slate-500">Créez une colonne dont le résultat est calculé à partir d'autres colonnes.</p>
          </div>
        </div>
        <button type="button" onClick={onAnnuler} aria-label="Fermer l'éditeur de calcul" className="rounded-full p-1.5 text-slate-400 hover:bg-white hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <div>
          <label htmlFor="calcul-nom" className={LABEL}>Nom de la colonne calculée</label>
          <input id="calcul-nom" type="text" value={nom} onChange={(e) => setNom(e.target.value)} className={INPUT} placeholder="Ex : Prime de 40 %" />
        </div>
        <div>
          <span className={LABEL}>Partir d'un exemple</span>
          <div className="flex flex-wrap gap-2">
            {DEPARTS_CALCUL.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setJetons(d.jetons())}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-sm"
              >
                <span className="block font-medium text-slate-800">{d.label}</span>
                <span className="block text-[11px] text-slate-400">{d.aide}</span>
              </button>
            ))}
            <button type="button" onClick={() => setJetons([])} className="rounded-xl border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500 hover:border-slate-400 hover:text-slate-700">
              Formule libre
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-sky-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-start gap-3">
          <div className="rounded-xl bg-sky-100 p-2 text-sky-700"><Calendar className="h-5 w-5" /></div>
          <div>
            <h4 className="font-semibold text-slate-900">Assistant professionnel — dates & heures</h4>
            <p className="mt-1 text-sm text-slate-500">Calculez une ancienneté, un délai entre deux dates, une date future ou des durées de travail sans écrire de formule.</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className={LABEL}>Type de calcul</label>
            <select value={outilTemps} onChange={(e) => { setOutilTemps(e.target.value); setChampTempsA(''); setChampTempsB(''); }} className={INPUT}>
              <option value="difference-dates">Durée entre deux dates</option>
              <option value="ajouter-jours">Ajouter des jours à une date</option>
              <option value="difference-heures">Écart entre deux valeurs d'heures</option>
              <option value="addition-heures">Additionner deux durées en heures</option>
              <option value="heures-en-minutes">Convertir des heures en minutes</option>
              <option value="minutes-en-heures">Convertir des minutes en heures</option>
            </select>
          </div>
          <div>
            <label className={LABEL}>{outilTemps === 'difference-dates' ? 'Unité du résultat' : 'Unité'}</label>
            {outilTemps === 'difference-dates' ? (
              <select value={uniteTemps} onChange={(e) => setUniteTemps(e.target.value)} className={INPUT}>
                <option value="jours">Jours</option><option value="semaines">Semaines</option><option value="mois">Mois (approximation calendaire)</option><option value="annees">Années (approximation calendaire)</option>
              </select>
            ) : <p className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">{outilTemps.includes('heures') || outilTemps.includes('minutes') ? 'Le résultat est exprimé en heures ou en minutes selon l’opération.' : 'Le résultat sera une date au format AAAA-MM-JJ.'}</p>}
          </div>
          <div>
            <label className={LABEL}>{outilTemps.includes('dates') || outilTemps === 'ajouter-jours' ? 'Date de départ' : outilTemps === 'minutes-en-heures' ? 'Colonne contenant des minutes' : 'Première colonne d’heures'}</label>
            {(outilTemps.includes('dates') || outilTemps === 'ajouter-jours') ? (
              <>
                <select value={modeDateA} onChange={(e) => setModeDateA(e.target.value)} className={`${INPUT} mb-2`}>
                  <option value="colonne">Utiliser une date d’une colonne</option><option value="saisie">Saisir une date manuellement</option>
                </select>
                {modeDateA === 'saisie' ? <input aria-label="Date de départ saisie manuellement" type="date" value={dateSaisieA} onChange={(e) => setDateSaisieA(e.target.value)} className={INPUT} /> : (
                  <select value={champTempsA} onChange={(e) => setChampTempsA(e.target.value)} className={INPUT}>
                    <option value="">Sélectionner une colonne…</option>{champsDates.map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
                  </select>
                )}
              </>
            ) : (
              <select value={champTempsA} onChange={(e) => setChampTempsA(e.target.value)} className={INPUT}>
                <option value="">Sélectionner une colonne…</option>{champsTemps.map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
              </select>
            )}
          </div>
          {['difference-dates', 'difference-heures', 'addition-heures'].includes(outilTemps) && (
            <div>
              <label className={LABEL}>{outilTemps === 'difference-dates' ? 'Date de fin / comparaison' : 'Deuxième colonne d’heures'}</label>
              {outilTemps === 'difference-dates' ? (
                <>
                  <select value={modeDateB} onChange={(e) => setModeDateB(e.target.value)} className={`${INPUT} mb-2`}>
                    <option value="colonne">Utiliser une date d’une colonne</option><option value="saisie">Saisir une date manuellement</option>
                  </select>
                  {modeDateB === 'saisie' ? <input aria-label="Date de fin saisie manuellement" type="date" value={dateSaisieB} onChange={(e) => setDateSaisieB(e.target.value)} className={INPUT} /> : (
                    <select value={champTempsB} onChange={(e) => setChampTempsB(e.target.value)} className={INPUT}>
                      <option value="">Sélectionner une colonne…</option>{champsDates.map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
                    </select>
                  )}
                </>
              ) : (
                <select value={champTempsB} onChange={(e) => setChampTempsB(e.target.value)} className={INPUT}>
                  <option value="">Sélectionner une colonne…</option>{champsHeures.map(([key, c]) => <option key={key} value={key}>{c.label}</option>)}
                </select>
              )}
            </div>
          )}
          {outilTemps === 'ajouter-jours' && <div><label className={LABEL}>Nombre de jours à ajouter (négatif pour soustraire)</label><input inputMode="numeric" value={nombreJours} onChange={(e) => setNombreJours(e.target.value)} className={INPUT} /></div>}
        </div>
        <button type="button" onClick={ajouterCalculTemps} disabled={((outilTemps.includes('dates') || outilTemps === 'ajouter-jours') ? (modeDateA === 'saisie' ? !dateSaisieA : !champTempsA) : !champTempsA) || (outilTemps === 'difference-dates' && (modeDateB === 'saisie' ? !dateSaisieB : !champTempsB)) || (['difference-heures', 'addition-heures'].includes(outilTemps) && !champTempsB) || (outilTemps === 'ajouter-jours' && (nombreJours === '' || !Number.isFinite(versNombre(nombreJours))))} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40">
          <Plus className="h-4 w-4" /> Ajouter ce calcul à la formule
        </button>
        <p className="mt-2 text-xs text-slate-500">Les durées entre dates sont calculées en jours écoulés; mois et années sont des équivalences moyennes. Les heures doivent être des valeurs numériques (ex. 7,5 pour 7 h 30).</p>
      </div>

      <div className="mt-5 rounded-2xl border border-fuchsia-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-start gap-3">
          <div className="rounded-xl bg-fuchsia-100 p-2 text-fuchsia-700"><GitBranch className="h-5 w-5" /></div>
          <div>
            <h4 className="font-semibold text-slate-900">Assistant de conditions — sans programmation</h4>
            <p className="mt-1 text-sm text-slate-500">Créez une règle, puis choisissez librement ce que le calcul doit retourner si elle est vraie ou fausse. Vous pouvez combiner des colonnes, des montants et des opérations.</p>
          </div>
        </div>
        <div className="mb-3">
          <label className={LABEL}>Où ajouter cette condition ?</label>
          <select value={modeAjoutCondition} onChange={(e) => setModeAjoutCondition(e.target.value)} className={INPUT}>
            <option value="addition">Ajouter au calcul (additionner son résultat)</option>
            <option value="dans-alors">Dans le résultat ALORS de la dernière condition</option>
            <option value="dans-sinon">Dans le résultat SINON de la dernière condition</option>
          </select>
          {modeAjoutCondition !== 'addition' && <p className="mt-1 text-xs text-amber-700">La nouvelle règle remplacera le résultat de la branche choisie. C'est ainsi que vous créez une condition dans une autre condition.</p>}
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
          <p className="mb-2 text-sm font-semibold text-amber-900">1. SI — Quelle règle faut-il vérifier ?</p>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className={LABEL}>Colonne à vérifier</label>
              <select value={champCondition} onChange={(e) => setChampCondition(e.target.value)} className={INPUT}>
                <option value="">Choisir une colonne…</option>
                {champsNumeriques.map((g) => <optgroup key={g.id} label={g.label}>{g.champs.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Comparaison</label>
              <select value={comparateurCondition} onChange={(e) => setComparateurCondition(e.target.value)} className={INPUT}>
                {BOUTON_COMP.map(([v, _s, nomComp]) => <option key={v} value={v}>{nomComp}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Comparer avec</label>
              <select value={typeValeurDroite} onChange={(e) => setTypeValeurDroite(e.target.value)} className={INPUT}>
                <option value="fixe">Un montant / nombre</option><option value="colonne">Une autre colonne</option>
              </select>
              {typeValeurDroite === 'fixe' ? <input inputMode="decimal" value={seuilCondition} onChange={(e) => setSeuilCondition(e.target.value)} className={`${INPUT} mt-2`} placeholder="Ex. 75000" /> : <select value={champValeurDroite} onChange={(e) => setChampValeurDroite(e.target.value)} className={`${INPUT} mt-2`}><option value="">Choisir la colonne à comparer…</option>{champsNumeriques.map((g) => <optgroup key={g.id} label={g.label}>{g.champs.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}</select>}
            </div>
          </div>
        </div>
        {[
          { key: 'alors', title: '2. ALORS — Si la règle est vraie', cfg: alorsExpression, setCfg: setAlorsExpression, color: 'emerald' },
          { key: 'sinon', title: '3. SINON — Si la règle est fausse', cfg: sinonExpression, setCfg: setSinonExpression, color: 'rose' },
        ].map(({ key, title, cfg, setCfg, color }) => (
          <div key={key} className={`mt-3 rounded-xl border p-3 ${color === 'emerald' ? 'border-emerald-200 bg-emerald-50/60' : 'border-rose-200 bg-rose-50/60'}`}>
            <p className={`mb-2 text-sm font-semibold ${color === 'emerald' ? 'text-emerald-900' : 'text-rose-900'}`}>{title}</p>
            <label className={LABEL}>Que faut-il retourner ?</label>
            <select value={cfg.type} onChange={(e) => setCfg({ ...cfg, type: e.target.value })} className={INPUT}>
              <option value="colonne">La valeur d'une colonne</option>
              <option value="fixe">Un montant fixe (ex. 0, 75 000)</option>
              <option value="pourcentage">Un pourcentage d'une colonne</option>
              <option value="calcul">Un calcul avec deux éléments (+, −, ×, ÷)</option>
            </select>
            {cfg.type === 'colonne' && <select value={cfg.champ} onChange={(e) => setCfg({ ...cfg, champ: e.target.value })} className={`${INPUT} mt-2`}><option value="">Utiliser la colonne de la règle par défaut</option>{champsNumeriques.map((g) => <optgroup key={g.id} label={g.label}>{g.champs.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}</select>}
            {cfg.type === 'fixe' && <input inputMode="decimal" value={cfg.valeur} onChange={(e) => setCfg({ ...cfg, valeur: e.target.value })} className={`${INPUT} mt-2`} placeholder="Saisir un nombre, ex. 0" />}
            {cfg.type === 'pourcentage' && <div className="mt-2 grid gap-2 sm:grid-cols-2"><div><label className={LABEL}>Colonne sur laquelle appliquer le taux</label><select value={cfg.champ} onChange={(e) => setCfg({ ...cfg, champ: e.target.value })} className={INPUT}><option value="">Utiliser la colonne de la règle</option>{champsNumeriques.map((g) => <optgroup key={g.id} label={g.label}>{g.champs.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}</select></div><div><label className={LABEL}>Taux en pourcentage (%)</label><input inputMode="decimal" value={cfg.taux} onChange={(e) => setCfg({ ...cfg, taux: e.target.value })} className={INPUT} placeholder="Ex. 7,7" /></div></div>}
            {cfg.type === 'calcul' && <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto_1fr]"><div className="rounded-lg bg-white/80 p-2"><label className={LABEL}>Premier élément</label><select value={cfg.aType} onChange={(e) => setCfg({ ...cfg, aType: e.target.value })} className={INPUT}><option value="colonne">Colonne</option><option value="fixe">Nombre</option></select>{cfg.aType === 'colonne' ? <select value={cfg.aChamp} onChange={(e) => setCfg({ ...cfg, aChamp: e.target.value })} className={`${INPUT} mt-2`}><option value="">Colonne de la règle</option>{champsNumeriques.map((g) => <optgroup key={g.id} label={g.label}>{g.champs.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}</select> : <input inputMode="decimal" value={cfg.aValeur} onChange={(e) => setCfg({ ...cfg, aValeur: e.target.value })} className={`${INPUT} mt-2`} placeholder="Nombre" />}</div><div className="self-center"><label className={LABEL}>Opération</label><select value={cfg.op} onChange={(e) => setCfg({ ...cfg, op: e.target.value })} className={`${INPUT} min-w-24`}><option value="+">+ Addition</option><option value="-">− Soustraction</option><option value="*">× Multiplication</option><option value="/">÷ Division</option></select></div><div className="rounded-lg bg-white/80 p-2"><label className={LABEL}>Deuxième élément</label><select value={cfg.bType} onChange={(e) => setCfg({ ...cfg, bType: e.target.value })} className={INPUT}><option value="fixe">Nombre</option><option value="colonne">Colonne</option></select>{cfg.bType === 'colonne' ? <select value={cfg.bChamp} onChange={(e) => setCfg({ ...cfg, bChamp: e.target.value })} className={`${INPUT} mt-2`}><option value="">Colonne de la règle</option>{champsNumeriques.map((g) => <optgroup key={g.id} label={g.label}>{g.champs.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>)}</select> : <input inputMode="decimal" value={cfg.bValeur} onChange={(e) => setCfg({ ...cfg, bValeur: e.target.value })} className={`${INPUT} mt-2`} placeholder="Nombre (ex. 0,077 pour 7,7 %)" />}</div></div>}
          </div>
        ))}
        <div className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
          <span className="font-semibold">Résumé :</span> si la règle est vraie, le calcul renverra le résultat ALORS ; sinon, il renverra le résultat SINON. Les résultats peuvent être des colonnes, des nombres, des pourcentages ou des opérations.
        </div>
        <button type="button" onClick={ajouterConditionGuidee} disabled={!champCondition || (typeValeurDroite === 'fixe' && (seuilCondition === '' || !Number.isFinite(versNombre(seuilCondition)))) || (typeValeurDroite === 'colonne' && !champValeurDroite) || !expressionValide(alorsExpression) || !expressionValide(sinonExpression)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:opacity-40">
          <Plus className="h-4 w-4" /> Ajouter la condition à la formule
        </button>
        <p className="mt-2 text-xs text-slate-500">Ajoutez plusieurs conditions pour les additionner, ou choisissez d'insérer une condition dans ALORS/SINON pour créer des règles imbriquées. La formule complète reste modifiable ci-dessous.</p>
      </div>

      <div className="mt-4">
        <span className={LABEL}>Votre formule</span>
        <div className="flex min-h-[4.5rem] flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
          {jetons.length === 0 && <span className="text-sm text-slate-400">Choisissez un exemple ci-dessus ou ajoutez des éléments avec les boutons ci-dessous.</span>}
          {jetons.map((j, i) => (
            <span key={i} className={`${classePuce} ${j.t === 'col' ? 'border-blue-300 bg-blue-50' : j.t === 'num' ? 'border-slate-300 bg-slate-100' : 'border-transparent bg-transparent shadow-none'}`}>
              {j.t === 'col' && (
                <select
                  aria-label="Colonne"
                  value={j.champ}
                  onChange={(e) => maj(i, { champ: e.target.value })}
                  className="max-w-[14rem] rounded-lg bg-transparent px-1.5 py-1 text-sm font-medium text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <option value="">Choisir une colonne…</option>
                  {j.champ && !connus.has(j.champ) && <option value={j.champ}>{libelleDe(j.champ)}</option>}
                  {champsNumeriques.map((g) => (
                    <optgroup key={g.id} label={g.label}>
                      {g.champs.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
              {j.t === 'num' && (
                <input
                  aria-label="Nombre"
                  inputMode="decimal"
                  value={j.v}
                  onChange={(e) => maj(i, { v: e.target.value })}
                  placeholder="nombre"
                  className="w-20 rounded-lg bg-transparent px-1.5 py-1 text-right text-sm font-medium tabular-nums text-slate-800 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              )}
              {j.t === 'op' && (
                <select
                  aria-label="Opération"
                  value={j.v}
                  onChange={(e) => maj(i, { v: e.target.value })}
                  className="rounded-lg bg-transparent px-1.5 py-1 text-base font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  {BOUTON_OP.map(([v, s, nomOp]) => (
                    <option key={v} value={v}>{s} ({nomOp})</option>
                  ))}
                </select>
              )}
              {j.t === 'comp' && (
                <select
                  aria-label="Comparateur"
                  value={j.v}
                  onChange={(e) => maj(i, { v: e.target.value })}
                  className="rounded-lg bg-transparent px-1.5 py-1 text-base font-bold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  {BOUTON_COMP.map(([v, s, nomComp]) => (
                    <option key={v} value={v}>{s} ({nomComp})</option>
                  ))}
                </select>
              )}
              {j.t === 'oplog' && (
                <select
                  aria-label="Opérateur logique"
                  value={j.v}
                  onChange={(e) => maj(i, { v: e.target.value })}
                  className="rounded-lg bg-transparent px-1.5 py-1 text-base font-bold text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                >
                  {BOUTON_LOG.map(([v, s, nomLog]) => (
                    <option key={v} value={v}>{s} ({nomLog})</option>
                  ))}
                </select>
              )}
              {j.t === 'datecalc' && <span className="max-w-[20rem] rounded-lg bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-800">{formuleEnTexte([j], libelleDe)}</span>}
              {j.t === 'cond' && <span className="px-2 py-1 rounded-lg bg-fuchsia-100 text-fuchsia-700 font-bold">SI</span>}
              {j.t === 'motcle' && <span className={`px-2 py-1 rounded-lg font-bold ${j.v === 'ALORS' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{j.v}</span>}
              {j.t === 'par' && <span className="px-1 text-xl font-bold text-slate-500">{j.v}</span>}
              <button type="button" onClick={() => retirer(i)} aria-label="Retirer cet élément" className="rounded-full p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-500">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => ajouter({ t: 'col', champ: '' })} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100">
            <Plus className="h-4 w-4" /> Colonne
          </button>
          <button type="button" onClick={() => ajouter({ t: 'num', v: '' })} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100">
            <Plus className="h-4 w-4" /> Nombre
          </button>
          <span className="mx-1 h-6 w-px bg-slate-200" />
          {BOUTON_OP.map(([v, s, nomOp]) => (
            <button key={v} type="button" onClick={() => ajouter({ t: 'op', v })} aria-label={`Ajouter ${nomOp}`} title={nomOp} className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-lg font-bold text-slate-700 hover:border-blue-300 hover:bg-blue-50">
              {s}
            </button>
          ))}
          <span className="mx-1 h-6 w-px bg-slate-200" />
          {['(', ')'].map((p) => (
            <button key={p} type="button" onClick={() => ajouter({ t: 'par', v: p })} aria-label={p === '(' ? 'Ouvrir une parenthèse' : 'Fermer une parenthèse'} className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-lg font-bold text-slate-700 hover:border-blue-300 hover:bg-blue-50">
              {p}
            </button>
          ))}
          <span className="mx-1 h-6 w-px bg-slate-200" />
          <button type="button" onClick={() => ajouter({ t: 'cond', v: 'IF' })} aria-label="Ajouter une condition SI" title="Condition SI" className="h-9 w-9 rounded-lg border border-fuchsia-200 bg-fuchsia-50 text-lg font-bold text-fuchsia-700 hover:border-fuchsia-300 hover:bg-fuchsia-100">
            IF
          </button>
          {BOUTON_COMP.map(([v, s, nomComp]) => (
            <button key={v} type="button" onClick={() => ajouter({ t: 'comp', v })} aria-label={`Ajouter ${nomComp}`} title={nomComp} className="h-9 w-9 rounded-lg border border-amber-200 bg-amber-50 text-sm font-bold text-amber-700 hover:border-amber-300 hover:bg-amber-100">
              {s}
            </button>
          ))}
          {BOUTON_LOG.map(([v, s, nomLog]) => (
            <button key={v} type="button" onClick={() => ajouter({ t: 'oplog', v })} aria-label={`Ajouter ${nomLog}`} title={nomLog} className="h-9 w-9 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-bold text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100">
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        {jetons.length > 0 && (
          <p className="mb-2 text-sm text-slate-600">
            <span className="font-medium text-slate-800">En clair : </span>
            {formuleEnTexte(jetons, libelleDe)}
          </p>
        )}
        {verification.ok ? (
          <p className="flex flex-wrap items-center gap-2 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4" />
            Formule valide. Exemple de résultats :
            {echantillon.map((v, r) => (
              <span key={r} className="rounded-md bg-emerald-50 px-2 py-0.5 font-semibold tabular-nums">{formaterValeur(v, jetons.some((j) => j.t === 'datecalc' && j.op === 'ajouter-jours') ? 'date' : 'montant')}</span>
            ))}
            <span className="text-xs text-slate-400">(calculés sur des valeurs d'exemple)</span>
          </p>
        ) : (
          <p role="status" className="flex items-start gap-2 text-sm text-amber-700">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" /> {verification.erreur}
          </p>
        )}
        <p className="mt-2 text-xs text-slate-400">Une cellule vide compte pour 0. Une division par 0 laisse la cellule vide.</p>
      </div>

      <div className="mt-4 flex justify-end gap-3">
        <button type="button" onClick={onAnnuler} className="rounded-xl bg-slate-100 px-5 py-2.5 text-slate-700 transition-colors hover:bg-slate-200">Annuler</button>
        <button
          type="button"
          disabled={!verification.ok}
          onClick={() => onValider({ libelle: nom.trim() || 'Calcul', formule: normaliserJetons(jetons), typeResultat: jetons.some((j) => j.t === 'datecalc' && j.op === 'ajouter-jours') ? 'date' : 'montant' })}
          className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2.5 font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
        >
          {initial ? 'Enregistrer le calcul' : 'Ajouter à mon rapport'}
        </button>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  CONSTRUCTEUR DE RAPPORT PERSONNALISÉ
// ═══════════════════════════════════════════════════════════════════════

const ConstructeurRapport = ({
  familles, colonnes, setColonnes, titre, setTitre, typeExport, totaux, setTotaux,
  apercu, chargementApercu, erreurApercu, aPeriode, indisponibles,
}) => {
  const [recherche, setRecherche] = useState('');
  const [ouvertes, setOuvertes] = useState(() => (familles[0] ? { [familles[0].id]: true } : {}));
  const [glisse, setGlisse] = useState(null);
  const [survol, setSurvol] = useState(null);
  const [editeur, setEditeur] = useState(null); // null | { index: number | null }

  const indexChamps = useMemo(() => {
    const m = {};
    familles.forEach((f) => f.champs.forEach((c) => { m[c.key] = { ...c, famille: f.label }; }));
    return m;
  }, [familles]);
  const champsNumeriques = useMemo(
    () => familles
      .map((f) => ({ id: f.id, label: f.label, champs: f.champs.filter((c) => NUMERIQUES.includes(c.type) && !indisponibles.has(c.key)) }))
      .filter((g) => g.champs.length > 0),
    [familles, indisponibles]
  );
  const choisis = new Set(colonnes.map((c) => c.champ));
  const terme = normaliserTexte(recherche.trim());
  const ignorees = new Map((apercu?.ignorees || []).map((i) => [i.champ, i.raison]));

  const modeles = [
    ...MODELES_BASE,
    ...(familles.find((f) => f.id === 'patronales')?.specifique
      ? [{ id: 'charges', label: 'Charges patronales', icon: Landmark, champs: ['personnel.matricule', 'personnel.nom', ...familles.find((f) => f.id === 'patronales').champs.map((c) => c.key)] }]
      : []),
  ];

  const libelleDefaut = (cle) => indexChamps[cle]?.label || cle;
  const basculer = (c) => {
    if (choisis.has(c.key)) setColonnes(colonnes.filter((x) => x.champ !== c.key));
    else setColonnes([...colonnes, { champ: c.key, libelle: c.label }]);
  };
  const basculerFamille = (f) => {
    const dispo = f.champs.filter((c) => !indisponibles.has(c.key));
    const cles = dispo.map((c) => c.key);
    if (cles.every((k) => choisis.has(k))) setColonnes(colonnes.filter((x) => !cles.includes(x.champ)));
    else setColonnes([...colonnes, ...dispo.filter((c) => !choisis.has(c.key)).map((c) => ({ champ: c.key, libelle: c.label }))]);
  };
  const renommer = (i, valeur) => setColonnes(colonnes.map((c, k) => (k === i ? { ...c, libelle: valeur } : c)));
  const deplacer = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= colonnes.length) return;
    const copie = [...colonnes];
    [copie[i], copie[j]] = [copie[j], copie[i]];
    setColonnes(copie);
  };
  const retirer = (i) => setColonnes(colonnes.filter((_, k) => k !== i));
  const deposer = (dest) => {
    if (glisse === null || glisse === dest) return;
    const copie = [...colonnes];
    const [elt] = copie.splice(glisse, 1);
    copie.splice(dest, 0, elt);
    setColonnes(copie);
  };
  const appliquerModele = (m) => {
    if (colonnes.length > 0 && !window.confirm('Remplacer les colonnes actuelles par ce modèle ?')) return;
    setColonnes(m.champs.filter((k) => indexChamps[k] && !indisponibles.has(k)).map((k) => ({ champ: k, libelle: indexChamps[k].label })));
    if (!titre.trim()) setTitre(m.label);
  };
  const validerCalcul = ({ libelle, formule, typeResultat }) => {
    if (editeur.index === null) setColonnes([...colonnes, { champ: nouvelIdCalcul(), libelle, formule, type: typeResultat }]);
    else setColonnes(colonnes.map((c, k) => (k === editeur.index ? { ...c, libelle, formule, type: typeResultat } : c)));
    setEditeur(null);
  };

  const famillesVisibles = familles
    .map((f) => ({ ...f, visibles: terme ? f.champs.filter((c) => normaliserTexte(c.label).includes(terme)) : f.champs }))
    .filter((f) => f.visibles.length > 0);
  const estOuverte = (id) => Boolean(terme) || Boolean(ouvertes[id]);

  const libelleCalcul = (c) => c.libelle.trim() || 'Calcul';
  const nomsAffiches = colonnes.map((c) => (c.formule ? libelleCalcul(c) : c.libelle.trim() || libelleDefaut(c.champ)));
  const doublons = nomsAffiches.filter((n, i) => nomsAffiches.findIndex((m) => m.toLowerCase() === n.toLowerCase()) !== i);
  const sources = Array.from(new Set(colonnes.flatMap((c) => {
    const cles = c.formule ? c.formule.filter((j) => j.t === 'col').map((j) => j.champ) : [c.champ];
    return cles.map((k) => SOURCES_LABEL[indexChamps[k]?.source || k.split('.')[0]]).filter(Boolean);
  })));
  const colonnesApercu = colonnes
    .map((c, i) => ({ cle: c.champ, libelle: nomsAffiches[i], type: c.formule ? 'montant' : indexChamps[c.champ]?.type || 'texte' }))
    .filter((c) => !ignorees.has(c.cle));

  return (
    <div className="space-y-6">
      <div className="paie-card grid gap-4 p-5 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <div>
          <label htmlFor="perso-titre" className={LABEL}>Nom du rapport</label>
          <input id="perso-titre" type="text" value={titre} onChange={(e) => setTitre(e.target.value)} className={INPUT} placeholder="Ex : Salaires du mois par département" />
        </div>
        <div>
          <span className={LABEL}>Partir d'un modèle</span>
          <div className="flex flex-wrap gap-2">
            {modeles.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.id} type="button" onClick={() => appliquerModele(m)} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow-sm">
                  <Icon className="h-4 w-4" /> {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-label="Informations disponibles" className="paie-card min-w-0 p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-xs font-bold text-white shadow-sm shadow-blue-600/30">1</span>
            <h3 className="font-semibold text-slate-900">Choisissez les informations</h3>
          </div>
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher une information (ex : salaire, prime, banque…)" aria-label="Rechercher une information"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-9 text-sm transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            {recherche && (
              <button type="button" onClick={() => setRecherche('')} aria-label="Effacer la recherche" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="max-h-[34rem] space-y-2 paie-scroll overflow-y-auto pr-1">
            {famillesVisibles.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Aucune information ne correspond à « {recherche} ».</div>
            )}
            {famillesVisibles.map((f) => {
              const Icon = f.icon;
              const ouverte = estOuverte(f.id);
              const nbChoisis = f.champs.filter((c) => choisis.has(c.key)).length;
              const toutChoisi = f.champs.filter((c) => !indisponibles.has(c.key)).every((c) => choisis.has(c.key));
              return (
                <div key={f.id} className={`overflow-hidden rounded-2xl border transition-shadow ${ouverte ? 'border-blue-200 shadow-sm' : 'border-slate-200'}`}>
                  <button
                    type="button" onClick={() => setOuvertes({ ...ouvertes, [f.id]: !ouvertes[f.id] })} aria-expanded={ouverte}
                    className="flex w-full items-center gap-3 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                  >
                    <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow ${FAMILLE_STYLE[f.id]}`}><Icon className="h-5 w-5" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900">{f.label}</span>
                      <span className="block truncate text-xs text-slate-500">{f.desc}</span>
                    </span>
                    {nbChoisis > 0 && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold tabular-nums text-blue-700">{nbChoisis}/{f.champs.length}</span>}
                    <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform duration-300 ${ouverte ? 'rotate-180 text-blue-600' : ''}`} />
                  </button>
                  {ouverte && (
                    <div className="paie-fade-up border-t border-slate-100 bg-slate-50/60 p-3">
                      {f.note && <p className="mb-2 flex items-start gap-1.5 text-xs text-amber-700"><Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />{f.note}</p>}
                      <div className="mb-2 flex justify-end">
                        <button type="button" onClick={() => basculerFamille(f)} className="text-xs font-medium text-blue-600 hover:text-blue-800">{toutChoisi ? 'Tout retirer' : 'Tout ajouter'}</button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {f.visibles.map((c) => {
                          const actif = choisis.has(c.key);
                          const indispo = indisponibles.has(c.key);
                          return (
                            <button
                              key={c.key} type="button" aria-pressed={actif} disabled={indispo && !actif} onClick={() => basculer(c)}
                              title={indispo ? "Indisponible : cette colonne n'existe pas dans la base" : undefined}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all duration-200 ${
                                actif
                                  ? 'border-blue-600 bg-gradient-to-r from-blue-600 to-indigo-600 font-medium text-white shadow-md'
                                  : indispo
                                    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 line-through'
                                    : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {actif ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5 text-slate-400" />}
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section aria-label="Colonnes du rapport" className="paie-card min-w-0 p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-xs font-bold text-white shadow-sm shadow-blue-600/30">2</span>
              <h3 className="font-semibold text-slate-900">Organisez et renommez</h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs tabular-nums text-slate-500">{colonnes.length} colonne{colonnes.length > 1 ? 's' : ''}</span>
              {colonnes.length > 0 && (
                <button type="button" onClick={() => { setColonnes([]); setEditeur(null); }} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition-colors hover:text-red-500">
                  <Trash2 className="h-3 w-3" /> Tout effacer
                </button>
              )}
            </div>
          </div>

          {colonnes.length === 0 ? (
            <div className="flex min-h-[14rem] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-6 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-500"><Table className="h-6 w-6" /></div>
              <p className="font-medium text-slate-700">Votre rapport est vide</p>
              <p className="mt-1 max-w-xs text-sm text-slate-500">Cliquez sur une information à gauche pour l'ajouter, ou partez d'un modèle.</p>
            </div>
          ) : (
            <ol className="max-h-[34rem] space-y-2 paie-scroll overflow-y-auto pr-1">
              {colonnes.map((c, i) => {
                const info = indexChamps[c.champ];
                const modifie = !c.formule && c.libelle !== (info?.label ?? c.libelle);
                const raisonIgnoree = ignorees.get(c.champ);
                return (
                  <li
                    key={c.champ}
                    onDragOver={(e) => { e.preventDefault(); setSurvol(i); }}
                    onDrop={(e) => { e.preventDefault(); deposer(i); setGlisse(null); setSurvol(null); }}
                    className={`paie-fade flex items-center gap-2 rounded-xl border bg-white p-2.5 transition-all duration-200 ${glisse === i ? 'scale-[0.98] opacity-50' : ''} ${
                      raisonIgnoree ? 'border-red-300 bg-red-50/40' : survol === i && glisse !== null && glisse !== i ? 'border-blue-400 shadow-md ring-2 ring-blue-200' : c.formule ? 'border-fuchsia-200' : 'border-slate-200'
                    }`}
                  >
                    <span
                      draggable
                      onDragStart={(e) => {
                        setGlisse(i);
                        if (e.dataTransfer) {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData?.('text/plain', String(i));
                          const li = e.currentTarget.closest('li');
                          if (li && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(li, 16, 16);
                        }
                      }}
                      onDragEnd={() => { setGlisse(null); setSurvol(null); }}
                      title="Glisser pour réordonner" aria-hidden="true"
                      className="cursor-grab touch-none rounded p-1 text-slate-300 hover:text-slate-500 active:cursor-grabbing"
                    >
                      <GripVertical className="h-5 w-5" />
                    </span>
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold tabular-nums text-slate-500">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <input
                        type="text" value={c.libelle} onChange={(e) => renommer(i, e.target.value)}
                        aria-label={`Nom de la colonne : ${c.formule ? libelleCalcul(c) : info?.label || c.champ}`}
                        placeholder={c.formule ? 'Calcul' : libelleDefaut(c.champ)}
                        className="w-full rounded-lg border border-transparent bg-slate-50 px-2.5 py-1.5 text-sm font-medium text-slate-900 transition-all hover:border-slate-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 px-1 text-[11px] text-slate-400">
                        {c.formule ? (
                          <span className="inline-flex min-w-0 items-center gap-1 text-fuchsia-600">
                            <Calculator className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">Calcul › {formuleEnTexte(c.formule, libelleDefaut)}</span>
                          </span>
                        ) : (
                          <span className="truncate">{info ? `${info.famille} › ${info.label}` : c.champ}</span>
                        )}
                        {modifie && (
                          <button type="button" onClick={() => renommer(i, info?.label ?? c.libelle)} className="inline-flex items-center gap-0.5 font-medium text-blue-500 hover:text-blue-700">
                            <RotateCcw className="h-3 w-3" /> Nom d'origine
                          </button>
                        )}
                        {raisonIgnoree && <span title={raisonIgnoree} className="font-semibold text-red-600">Non disponible : ignorée</span>}
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center">
                      {c.formule && (
                        <button type="button" onClick={() => setEditeur({ index: i })} aria-label={`Modifier le calcul ${nomsAffiches[i]}`} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-fuchsia-50 hover:text-fuchsia-600">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      <button type="button" onClick={() => deplacer(i, -1)} disabled={i === 0} aria-label={`Monter ${nomsAffiches[i]}`} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"><ChevronUp className="h-4 w-4" /></button>
                      <button type="button" onClick={() => deplacer(i, 1)} disabled={i === colonnes.length - 1} aria-label={`Descendre ${nomsAffiches[i]}`} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"><ChevronDown className="h-4 w-4" /></button>
                      <button type="button" onClick={() => retirer(i)} aria-label={`Retirer ${nomsAffiches[i]}`} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="mt-3 space-y-3">
            <button
              type="button" onClick={() => setEditeur({ index: null })}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/50 px-4 py-3 text-sm font-semibold text-fuchsia-700 transition-all hover:-translate-y-0.5 hover:border-fuchsia-400 hover:bg-fuchsia-50 hover:shadow-sm"
            >
              <Calculator className="h-4 w-4" /> Ajouter un calcul (somme, pourcentage, condition SI…)
            </button>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 transition-colors hover:border-blue-300">
              <input type="checkbox" checked={totaux} onChange={(e) => setTotaux(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span>Ajouter une <strong>ligne de totaux</strong> <span className="text-slate-400">(somme des colonnes numériques)</span></span>
            </label>
          </div>

          {doublons.length > 0 && (
            <p role="alert" className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800"><Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> Deux colonnes portent le même nom : pensez à les distinguer.</p>
          )}
          {typeExport === 'pdf' && colonnes.length > 10 && (
            <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800"><Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> Au-delà de 10 colonnes, le format Excel offre une meilleure lisibilité qu'un PDF.</p>
          )}
        </section>
      </div>

      {editeur && (
        <EditeurCalcul
          key={editeur.index === null ? 'nouveau' : colonnes[editeur.index]?.champ}
          champsNumeriques={champsNumeriques}
          libelleDe={libelleDefaut}
          indexChamps={indexChamps}
          initial={editeur.index === null ? null : colonnes[editeur.index]}
          onValider={validerCalcul}
          onAnnuler={() => setEditeur(null)}
        />
      )}

      <section aria-label="Aperçu du rapport" className="paie-card p-5">
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 text-xs font-bold text-white shadow-sm shadow-blue-600/30">3</span>
          <h3 className="font-semibold text-slate-900">Vérifiez l'aperçu</h3>
          <Eye className="h-4 w-4 text-slate-400" />
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2.5">
            <span className="truncate text-sm font-semibold text-slate-800">{titre.trim() || 'Rapport personnalisé'}</span>
            {chargementApercu ? (
              <span className="flex-shrink-0 rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">Chargement...</span>
            ) : apercu ? (
              <span className="flex-shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-medium text-green-700">Données réelles</span>
            ) : (
              <span className="flex-shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">En attente</span>
            )}
          </div>
          {colonnes.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">L'aperçu apparaît dès que vous ajoutez une colonne.</p>
          ) : !aPeriode ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Sélectionnez une période de paie pour voir les données.</p>
          ) : chargementApercu && !apercu ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
          ) : erreurApercu ? (
            <p role="alert" className="px-4 py-8 text-center text-sm text-red-600">{erreurApercu}</p>
          ) : apercu && apercu.lignes.length > 0 ? (
            <TableauDonnees colonnes={colonnesApercu} lignes={apercu.lignes} totaux={totaux ? apercu.totaux : null} />
          ) : apercu ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Aucune ligne pour cette période avec ces filtres.</p>
          ) : null}
          {(ignorees.size > 0 || sources.length > 0 || apercu) && (
            <div className="space-y-2 border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 text-xs text-slate-500">
              {ignorees.size > 0 && (
                <p role="alert" className="flex items-start gap-1.5 font-medium text-red-600">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  {ignorees.size} colonne{ignorees.size > 1 ? 's' : ''} non prise{ignorees.size > 1 ? 's' : ''} en compte : {[...ignorees.entries()].map(([champ, raison]) => `${libelleDefaut(champ)} (${raison})`).join(' · ')}
                </p>
              )}
              {apercu && <p>Aperçu des {Math.min(10, apercu.lignes.length)} premières lignes sur {apercu.total} au total.</p>}
              {sources.length > 0 && (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5 text-blue-500" />
                  <span className="font-medium text-slate-600">Données reliées automatiquement :</span>
                  {sources.map((s, i) => (
                    <span key={s} className="inline-flex items-center gap-1.5">
                      {i > 0 && <span className="text-slate-300">⟷</span>}
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700">{s}</span>
                    </span>
                  ))}
                </span>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  RÉSULTAT D'UNE GÉNÉRATION
// ═══════════════════════════════════════════════════════════════════════

const PanneauResultat = ({ resultat, onFermer, onTelecharger }) => {
  const sommes = resultat.totaux
    ? resultat.colonnes.filter((c) => c.cle in resultat.totaux).slice(-4).map((c) => ({ ...c, valeur: resultat.totaux[c.cle] }))
    : [];
  return (
    <section aria-label="Résultat du rapport" className="paie-card paie-fade-up overflow-hidden">
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-white">
        <div className="pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
              <CheckCircle className="paie-pop h-6 w-6" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-100/80">Rapport généré</div>
              <h3 className="truncate text-lg font-bold tracking-tight">{resultat.titre}</h3>
              <p className="text-sm text-white/85">
                {[resultat.periode?.libelle_paie, resultat.periode?.filiale_libelle].filter(Boolean).join(' · ')} — {resultat.total} ligne{resultat.total > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => onTelecharger('excel')} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-emerald-700 shadow-lg shadow-emerald-900/20 transition-all hover:-translate-y-0.5 hover:shadow-xl">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button type="button" onClick={() => onTelecharger('pdf')} className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-bold text-white ring-1 ring-white/35 transition-all hover:-translate-y-0.5 hover:bg-white/25">
              <Printer className="h-4 w-4" /> PDF
            </button>
            <button type="button" onClick={onFermer} aria-label="Fermer le résultat" className="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
        </div>
      </div>
      {sommes.length > 0 && (
        <div className="grid grid-cols-2 gap-3 border-b border-slate-100 bg-slate-50/60 p-4 lg:grid-cols-4">
          {sommes.map((s) => (
            <div key={s.cle} className="rounded-xl bg-white p-3.5 ring-1 ring-slate-200/70">
              <div className="truncate text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.libelle}</div>
              <div className="mt-1 text-xl font-extrabold tabular-nums tracking-tight text-nuit">{formaterValeur(s.valeur, s.type)}</div>
            </div>
          ))}
        </div>
      )}
      {resultat.ignorees?.length > 0 && (
        <p role="alert" className="flex items-start gap-2 border-b border-amber-100 bg-amber-50 px-6 py-3 text-sm text-amber-800">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {resultat.ignorees.length} colonne{resultat.ignorees.length > 1 ? 's' : ''} non disponible{resultat.ignorees.length > 1 ? 's' : ''} pour cette période : {resultat.ignorees.map((i) => `${i.libelle || i.champ} (${i.raison})`).join(' · ')}
        </p>
      )}
      <TableauDonnees colonnes={resultat.colonnes} lignes={resultat.lignes} totaux={resultat.totaux} maxLignes={25} />
      {resultat.lignes.length > 25 && (
        <p className="border-t border-slate-100 bg-slate-50/60 px-6 py-2.5 text-xs text-slate-500">Affichage des 25 premières lignes : les fichiers Excel et PDF contiennent les {resultat.total} lignes.</p>
      )}
    </section>
  );
};

const ExportEtats = () => {
  const { currentFiliale, filialeId, isConsolidated } = useFiliale();
  const [loading, setLoading] = useState(false);
  const [paies, setPaies] = useState([]);
  const [selectedPaie, setSelectedPaie] = useState(null);
  const [typePaie, setTypePaie] = useState('normale');
  const [typeExport, setTypeExport] = useState('pdf');
  const [filialeFilter, setFilialeFilter] = useState(filialeId || 'all');
  const [filtres, setFiltres] = useState({
    typePersonnel: 'all',
    departement: 'all',
    statut: 'all',
    businessUnit: 'all',
    marque: 'all',
    typeContrat: 'all'
  });
  const [typesPersonnel, setTypesPersonnel] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [marques, setMarques] = useState([]);
  const [typesContrat, setTypesContrat] = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [configTab, setConfigTab] = useState('filtres');
  const [colonnesPerso, setColonnesPerso] = useState([]);
  const [titrePerso, setTitrePerso] = useState('');
  const [totauxPerso, setTotauxPerso] = useState(false);
  const [filialesOuvertes, setFilialesOuvertes] = useState({});
  const [apercu, setApercu] = useState(null);
  const [chargementApercu, setChargementApercu] = useState(false);
  const [erreurApercu, setErreurApercu] = useState('');
  const [resultat, setResultat] = useState(null);
  const [indisponibles, setIndisponibles] = useState(() => new Set());
  const [configsSauvegardees, setConfigsSauvegardees] = useState([]);
  const [modalSauvegarde, setModalSauvegarde] = useState(false);
  const [nomConfig, setNomConfig] = useState('');
  const [descriptionConfig, setDescriptionConfig] = useState('');
  const [configEnEdition, setConfigEnEdition] = useState(null); // ID de la config en édition
  const catalogRef = useRef(null);

  // Code de la filiale courante : les rapports déclarent des CODES (BJ, CI, GN…), jamais des ids
  const codeFiliale = normaliserCode(currentFiliale?.code);
  const famillesPerso = useMemo(() => construireFamilles(codeFiliale, isConsolidated), [codeFiliale, isConsolidated]);

  // Charger les configurations sauvegardées
  const loadConfigs = async () => {
    try {
      const filialeParam = filialeId === 'all' || filialeId === null ? '' : filialeId;
      const res = await http.get(`/api/finance/config-rapports${filialeParam ? `?filiale_id=${filialeParam}` : ''}`);
      setConfigsSauvegardees(res.data.configs || []);
    } catch (error) {
      console.error('Erreur chargement configurations:', error);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleSauvegarderConfig = async () => {
    if (!nomConfig.trim()) {
      alert('Veuillez saisir un nom pour la configuration');
      return;
    }
    try {
      if (configEnEdition) {
        // Modification d'une config existante
        await http.put(`/api/finance/config-rapports/${configEnEdition}`, {
          titre: nomConfig.trim(),
          description: descriptionConfig.trim() || null,
          colonnes: colonnesPerso,
          filtres: filtres,
          options: { totaux: totauxPerso },
          filiale_id: filialeId === 'all' || filialeId === null ? null : filialeId,
        });
      } else {
        // Création d'une nouvelle config
        await http.post('/api/finance/config-rapports', {
          titre: nomConfig.trim(),
          description: descriptionConfig.trim() || null,
          colonnes: colonnesPerso,
          filtres: filtres,
          options: { totaux: totauxPerso },
          filiale_id: filialeId === 'all' || filialeId === null ? null : filialeId,
        });
      }
      setModalSauvegarde(false);
      setNomConfig('');
      setDescriptionConfig('');
      setConfigEnEdition(null);
      loadConfigs();
      alert(configEnEdition ? 'Configuration mise à jour avec succès' : 'Configuration sauvegardée avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde configuration:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleModifierConfig = (config) => {
    setConfigEnEdition(config.id);
    setNomConfig(config.titre);
    setDescriptionConfig(config.description || '');
    setColonnesPerso(config.colonnes || []);
    setTitrePerso(config.titre || '');
    if (config.filtres) setFiltres(config.filtres);
    if (config.options) setTotauxPerso(config.options.totaux || false);
    setModalSauvegarde(true);
  };

  const handleChargerConfig = async (config) => {
    if (!window.confirm(`Charger la configuration "${config.titre}" ? Cela remplacera votre sélection actuelle.`)) return;
    setColonnesPerso(config.colonnes || []);
    setTitrePerso(config.titre || '');
    if (config.filtres) setFiltres(config.filtres);
    if (config.options) setTotauxPerso(config.options.totaux || false);
  };

  const handleSupprimerConfig = async (configId) => {
    if (!window.confirm('Supprimer cette configuration ?')) return;
    try {
      await http.delete(`/api/finance/config-rapports/${configId}`);
      loadConfigs();
      alert('Configuration supprimée');
    } catch (error) {
      console.error('Erreur suppression configuration:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Corps de la requête, identique pour l'aperçu, la génération
  const construirePayload = (extra = {}) => ({
    paie_id: selectedPaie.idpaie,
    type_rapport: 'personnalise',
    type_export: typeExport,
    type_paie: typePaie,
    filiale_id: filialeFilter === 'all' ? null : filialeFilter,
    filtres: {
      type_personnel_id: filtres.typePersonnel === 'all' ? null : filtres.typePersonnel,
      departement_id: filtres.departement === 'all' ? null : filtres.departement,
      statut: filtres.statut === 'all' ? null : filtres.statut,
      business_unit_id: filtres.businessUnit === 'all' ? null : filtres.businessUnit,
      marque_id: filtres.marque === 'all' ? null : filtres.marque,
      type_contrat: filtres.typeContrat === 'all' ? null : filtres.typeContrat
    },
    titre: titrePerso,
    colonnes: colonnesPerso,
    options: { totaux: totauxPerso },
    ...extra
  });

  // Aperçu en données réelles : se met à jour (avec un léger délai) quand la sélection change.
  // Renommer une colonne ou le titre ne relance PAS de requête : seules les données comptent.
  const signatureApercu = JSON.stringify([
    selectedPaie?.idpaie,
    colonnesPerso.map((c) => [c.champ, c.formule]),
    totauxPerso, filialeFilter, filtres,
  ]);
  useEffect(() => {
    const pretApercu = selectedPaie && colonnesPerso.length > 0;
    if (!pretApercu) {
      setApercu(null);
      setErreurApercu('');
      setChargementApercu(false);
      return undefined;
    }
    let annule = false;
    const minuteur = setTimeout(async () => {
      setChargementApercu(true);
      setErreurApercu('');
      try {
        const res = await http.post('/api/finance/exporter-etat', construirePayload({ apercu: true, limite: 10 }));
        if (!annule) setApercu(res.data?.success ? res.data : null);
      } catch (error) {
        console.error('Erreur chargement aperçu:', error);
        if (!annule) {
          setApercu(null);
          setErreurApercu(error.response?.data?.message || "Impossible de charger l'aperçu");
        }
      } finally {
        if (!annule) setChargementApercu(false);
      }
    }, 400);
    return () => {
      annule = true;
      clearTimeout(minuteur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureApercu]);

  // Vérifie, auprès du serveur, que chaque colonne proposée existe bien dans la base
  useEffect(() => {
    let annule = false;
    http.get('/api/finance/champs')
      .then((res) => {
        const serveur = Array.isArray(res.data?.champs) ? res.data.champs : [];
        if (annule || serveur.length === 0) return;
        const etat = new Map(serveur.map((c) => [c.cle, c.disponible]));
        const manquants = construireFamilles('', true)
          .flatMap((f) => f.champs.map((c) => c.key))
          .filter((cle) => etat.get(cle) !== true);
        if (manquants.length > 0) {
          console.warn('[Export des états] Colonnes proposées mais indisponibles côté serveur :', manquants);
        }
        setIndisponibles(new Set(manquants));
      })
      .catch(() => {});
    return () => { annule = true; };
  }, []);

  // Types de rapports disponibles
  const typesRapports = [
    { 
      id: 'bulletin_paie', 
      label: 'Bulletins de paie', 
      icon: FileText,
      description: 'Générer les bulletins de paie individuels pour tous les employés',
      filiales: ['BJ', 'CI', 'CI_HOOPE', 'GN', 'GNB', 'CG', 'CM']
    },
    { 
      id: 'etat_charges', 
      label: 'État des charges patronales', 
      icon: Wallet,
      description: 'Synthèse des charges patronales par employé',
      filiales: ['BJ', 'CI', 'CI_HOOPE', 'GN', 'GNB', 'CG', 'CM']
    },
    { 
      id: 'etat_salarial', 
      label: 'État salarial global', 
      icon: Users,
      description: 'Vue d\'ensemble de la masse salariale',
      filiales: ['BJ', 'CI', 'CI_HOOPE', 'GN', 'GNB', 'CG', 'CM']
    },
    { 
      id: 'decompte_heures', 
      label: 'Décompte des heures', 
      icon: Calendar,
      description: 'Rapport détaillé des heures travaillées',
      filiales: ['BJ', 'CI', 'CI_HOOPE', 'GN', 'GNB', 'CG', 'CM']
    },
    { 
      id: 'declaration_cnss', 
      label: 'Déclaration CNSS', 
      icon: Building2,
      description: 'Déclaration de cotisations sociales',
      filiales: ['BJ', 'CG']
    },
    { 
      id: 'declaration_cnps', 
      label: 'Déclaration CNPS', 
      icon: Building2,
      description: 'Déclaration de cotisations CNPS',
      filiales: ['CI', 'CI_HOOPE']
    },
    { 
      id: 'declaration_vrsmt', 
      label: 'Déclaration VRSMT', 
      icon: Building2,
      description: 'Déclaration VRSMT',
      filiales: ['GN']
    },
    { 
      id: 'declaration_cm', 
      label: 'Déclaration charges Cameroun', 
      icon: Building2,
      description: 'Déclaration des charges patronales Cameroun',
      filiales: ['CM']
    }
  ];

  useEffect(() => {
    loadPaies();
    loadTypesPersonnel();
    loadDepartements();
    loadBusinessUnits();
    loadMarques();
    loadTypesContrat();
    loadFiliales();
    // Réinitialiser les filiales ouvertes quand on change de filiale
    setFilialesOuvertes({});
  }, [filialeFilter, isConsolidated, typePaie]);

  useEffect(() => {
    // Toujours synchroniser filialeFilter avec filialeId du contexte
    if (filialeId === 'all' || filialeId === null) {
      setFilialeFilter('all');
    } else if (filialeId) {
      setFilialeFilter(filialeId);
    }
  }, [filialeId]);

  const loadPaies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typePaie === 'interim') params.set('isInterim', 'true');
      if (isConsolidated) params.set('consolidated', '1');
      else params.set('filiale_id', filialeFilter === 'all' ? 'all' : filialeFilter);
      const res = await http.get(`/api/paies?${params}`);
      const data = Array.isArray(res.data) ? res.data : [];
      // paie_interime n'a pas de colonne idpaie : on normalise
      setPaies(data.map((p) => ({ ...p, idpaie: p.idpaie ?? p.idpaie_interime })));
    } catch (error) {
      console.error('Erreur chargement paies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTypesPersonnel = async () => {
    try {
      const endpoint = isConsolidated
        ? '/api/types-personnel'
        : `/api/types-personnel?filiale_id=${filialeFilter === 'all' ? 'all' : filialeFilter}`;
      const res = await http.get(endpoint);
      setTypesPersonnel(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement types personnel:', error);
    }
  };

  const loadDepartements = async () => {
    try {
      const res = await http.get('/api/departements');
      setDepartements(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement départements:', error);
    }
  };

  const loadBusinessUnits = async () => {
    try {
      const res = await http.get('/api/business-units');
      setBusinessUnits(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement business units:', error);
    }
  };

  const loadMarques = async () => {
    try {
      const res = await http.get('/api/marques');
      setMarques(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement marques:', error);
    }
  };

  const loadTypesContrat = async () => {
    try {
      const res = await http.get('/api/types-contrat');
      setTypesContrat(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement types contrat:', error);
    }
  };

  const loadFiliales = async () => {
    try {
      const res = await http.get('/api/filiales');
      setFiliales(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Erreur chargement filiales:', error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedPaie) {
      alert('Veuillez sélectionner une période de paie');
      return;
    }

    if (colonnesPerso.length === 0) {
      alert('Veuillez sélectionner au moins une colonne pour le rapport personnalisé');
      return;
    }

    setGenerating(true);
    try {
      const res = await http.post('/api/finance/exporter-etat', construirePayload());

      if (res.data.success) {
        // Données réelles de la base : affichées ci-dessous et téléchargées dans le format choisi
        setResultat(res.data);
        telechargerRapport(typeExport, res.data);
      } else {
        alert('Erreur lors de la génération du rapport: ' + (res.data.message || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur génération rapport:', error);
      const detail = error.response?.data?.message;
      alert('Erreur lors de la génération du rapport' + (detail ? ' : ' + detail : ''));
    } finally {
      setGenerating(false);
    }
  };

  const filtrerRapports = () => {
    // Plus utilisé car mode personnalisé uniquement
    return [];
  };

  const rapportsDisponibles = filtrerRapports();

  // Valeurs dérivées pour l'affichage (lecture seule de l'état existant)
  const nbFiltres = [filtres.typePersonnel, filtres.departement, filtres.statut, filtres.businessUnit, filtres.marque, filtres.typeContrat].filter((v) => v !== 'all').length;
  const pretPersonnalise = Boolean(selectedPaie && colonnesPerso.length > 0);
  const pret = pretPersonnalise;
  const formatLibelle = typeExport === 'pdf' ? 'PDF' : 'Excel';
  const manquants = [
    !selectedPaie && 'une période',
    colonnesPerso.length === 0 && 'au moins une colonne',
  ].filter(Boolean);
  const reinitialiserFiltres = () => setFiltres({ typePersonnel: 'all', departement: 'all', statut: 'all', businessUnit: 'all', marque: 'all', typeContrat: 'all' });

  // Périodes regroupées par filiale (clé = CODE de la filiale, jamais son id sauf si le code est absent)
  const groupesPaies = (() => {
    const map = new Map();
    paies.forEach((p) => {
      const cle = cleFiliale(p);
      if (!map.has(cle)) {
        const f = filiales.find((x) => String(x.idfiliale) === String(p.filiale_id));
        map.set(cle, {
          cle,
          code: normaliserCode(p.filiale_code || f?.code) || '???',
          libelle: p.filiale_libelle || f?.libelle || (p.filiale_id != null ? `Filiale ${p.filiale_id}` : 'Sans filiale'),
          paies: [],
        });
      }
      map.get(cle).paies.push(p);
    });
    return Array.from(map.values());
  })();

  const etapes = [
    { id: 'periode', label: 'Période', icon: Calendar, ok: Boolean(selectedPaie), valeur: selectedPaie?.libelle_paie || 'À choisir', onClick: () => {} },
    { id: 'rapport', label: 'Colonnes', icon: FileSpreadsheet, ok: colonnesPerso.length > 0, valeur: `${colonnesPerso.length} colonne${colonnesPerso.length > 1 ? 's' : ''}`, onClick: () => {} },
    { id: 'options', label: 'Format & filtres', icon: Filter, ok: true, valeur: `${formatLibelle} · ${nbFiltres} filtre${nbFiltres > 1 ? 's' : ''}`, onClick: () => {} },
    { id: 'generation', label: 'Génération', icon: Printer, ok: pret && !generating, encours: generating, valeur: generating ? 'En cours…' : pret ? 'Prêt à générer' : 'En attente' },
  ];
  const ongletsConfig = [
    { id: 'filtres', label: 'Filtres', icon: Filter, badge: nbFiltres > 0 ? nbFiltres : undefined },
    { id: 'format', label: 'Format', icon: Download, badge: typeExport === 'pdf' ? 'PDF' : 'XLSX' },
  ];

  const boutonPeriode = (p, compact) => {
    const actif = selectedPaie?.idpaie === p.idpaie;
    const etat = ETATS_PAIE[p.etat];
    return (
      <button
        key={p.idpaie}
        type="button"
        role="radio"
        aria-checked={actif}
        onClick={() => setSelectedPaie(p)}
        className={`flex w-full items-center gap-3 rounded-xl border-2 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${compact ? 'px-4 py-2.5' : 'px-4 py-3'} ${
          actif
            ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm'
            : 'border-slate-100 bg-white hover:border-blue-200 hover:bg-slate-50'
        }`}
      >
        <span
          className={`flex flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${compact ? 'h-4 w-4' : 'h-5 w-5'} ${
            actif ? 'border-blue-600 bg-blue-600' : 'border-slate-300'
          }`}
        >
          {actif && <Check className="paie-pop h-3 w-3 text-white" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block truncate font-semibold text-slate-900 ${compact ? 'text-xs' : 'text-sm'}`}>{p.libelle_paie}</span>
          <span className={`block tabular-nums text-slate-500 ${compact ? 'text-[11px]' : 'mt-0.5 text-xs'}`}>
            {formatDateFr(p.date_debut) || '—'} → {formatDateFr(p.date_fin) || '—'}
          </span>
        </span>
        {Number(p.etat) === 2 && (
          <span className="flex-shrink-0 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700 ring-1 ring-amber-200" title="Données archivées">
            Archive
          </span>
        )}
        {etat && (
          <span className={`flex-shrink-0 rounded-full px-2 py-0.5 font-medium ${compact ? 'text-[10px]' : 'text-[11px]'} ${etat.classe}`}>{etat.label}</span>
        )}
      </button>
    );
  };

  return (
    <>
    <div className="paie-root min-h-screen">
      <PaieStyles />
      <BlueHeader
        title="Export des États"
        subtitle="Génération et export des rapports financiers,  de paie et du personnel"
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadPaies,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Contexte */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2.5">
            {currentFiliale && (
              <div className="paie-card flex items-center gap-3 py-2 pl-2 pr-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-600/30">
                  <Building2 className="h-5 w-5" />
                </span>
                <span className="leading-tight">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Filiale</span>
                  <span className="block text-sm font-bold text-slate-900">{currentFiliale.libelle}</span>
                </span>
              </div>
            )}
            {isConsolidated && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3.5 py-2 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-200">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                Vue consolidée
              </span>
            )}
          </div>
          <p className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-2 text-xs font-medium text-slate-500 shadow-sm ring-1 ring-slate-200/70">
            <Clock className="h-3.5 w-3.5 text-slate-400" />
            <span><strong className="font-bold tabular-nums text-slate-800">{paies.length}</strong> période(s) de paie</span>
          </p>
        </div>

        {/* Parcours */}
        <div className="paie-fade-up mb-6 paie-card p-6">
          <Etapes etapes={etapes} />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
          {/* Sidebar gauche - Période + Filtres + Format */}
          <div className="space-y-6">
            {/* Sélection de la période */}
            <div className="paie-fade-up paie-card p-5" style={{ animationDelay: '60ms' }}>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100"><Calendar className="h-4 w-4" /></span>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Période de paie</h2>
                </div>
                {selectedPaie && (
                  <button type="button" onClick={() => setSelectedPaie(undefined)} className="text-xs font-medium text-slate-400 transition-colors hover:text-red-500">
                    Désélectionner
                  </button>
                )}
              </div>

              {/* Sélecteur de type de paie */}
              <div className="flex gap-2 rounded-xl bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setTypePaie('normale')}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${typePaie === 'normale' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Paie normale
                </button>
                <button
                  type="button"
                  onClick={() => setTypePaie('interim')}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${typePaie === 'interim' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Intérim
                </button>
              </div>

              {loading && paies.length === 0 ? (
                <div className="space-y-2" aria-hidden="true">
                  {[0, 1, 2].map((n) => (
                    <div key={n} className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  ))}
                </div>
              ) : paies.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
                  Aucune période de paie disponible
                </div>
              ) : filialeFilter === 'all' ? (
                // Mode toutes filiales : regrouper par filiale
                <div role="radiogroup" aria-label="Période de paie" className="max-h-[22rem] space-y-2 paie-scroll overflow-y-auto pr-1">
                  {groupesPaies.map((groupe) => {
                    const estOuverte = filialesOuvertes[groupe.cle];
                    return (
                      <div key={groupe.cle} className="overflow-hidden rounded-2xl border border-slate-200">
                        <button
                          type="button"
                          aria-expanded={Boolean(estOuverte)}
                          onClick={() => setFilialesOuvertes({ ...filialesOuvertes, [groupe.cle]: !estOuverte })}
                          className="flex w-full items-center gap-3 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                        >
                          <Building2 className="h-5 w-5 text-blue-600" />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-slate-900">{groupe.libelle}</span>
                            <span className="block text-xs text-slate-500">{groupe.paies.length} période(s)</span>
                          </span>
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-700">{groupe.code}</span>
                          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform duration-300 ${estOuverte ? 'rotate-180' : ''}`} />
                        </button>
                        {estOuverte && (
                          <div className="paie-fade-up border-t border-slate-100 bg-slate-50/60 p-2 space-y-1">
                            {groupe.paies.map((p) => boutonPeriode(p, true))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Mode filiale spécifique : affichage simple
                <div role="radiogroup" aria-label="Période de paie" className="max-h-[22rem] space-y-2 paie-scroll overflow-y-auto pr-1">
                  {paies.map((p) => boutonPeriode(p, false))}
                </div>
              )}
            </div>

            {/* Configuration - Onglets */}
            <div className="paie-fade-up paie-card p-5" style={{ animationDelay: '120ms' }}>
              <SlidingTabs tabs={ongletsConfig} active={configTab} onChange={setConfigTab} label="Configuration de l'export" />

              <div key={configTab} className="paie-fade-up mt-5">
                {configTab === 'filtres' && (
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100"><Filter className="h-4 w-4" /></span>
                        <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Filtres</h2>
                      </div>
                      {nbFiltres > 0 && (
                        <button type="button" onClick={reinitialiserFiltres} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 transition-colors hover:text-blue-600">
                          <RotateCcw className="h-3 w-3" /> Réinitialiser
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="filtre-type-personnel" className={LABEL}>Type de personnel</label>
                        <select id="filtre-type-personnel" value={filtres.typePersonnel} onChange={(e) => setFiltres({...filtres, typePersonnel: e.target.value})} className={INPUT}>
                          <option value="all">Tous</option>
                          {typesPersonnel.map(t => (
                            <option key={t.idtype_personnel} value={t.idtype_personnel}>{t.libelle}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="filtre-departement" className={LABEL}>Département</label>
                        <select id="filtre-departement" value={filtres.departement} onChange={(e) => setFiltres({...filtres, departement: e.target.value})} className={INPUT}>
                          <option value="all">Tous</option>
                          {departements.filter(d => d.libelle_departement && d.libelle_departement.trim() !== '').map(d => (
                            <option key={d.iddepartement} value={d.iddepartement}>{d.libelle_departement}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="filtre-business-unit" className={LABEL}>Business Unit</label>
                        <select id="filtre-business-unit" value={filtres.businessUnit} onChange={(e) => setFiltres({...filtres, businessUnit: e.target.value})} className={INPUT}>
                          <option value="all">Toutes</option>
                          {businessUnits.map(bu => (
                            <option key={bu.idbusiness_unit} value={bu.idbusiness_unit}>{bu.libelle}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="filtre-marque" className={LABEL}>Marque</label>
                        <select id="filtre-marque" value={filtres.marque} onChange={(e) => setFiltres({...filtres, marque: e.target.value})} className={INPUT}>
                          <option value="all">Toutes</option>
                          {marques.map(m => (
                            <option key={m.id} value={m.id}>{m.libelle}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="filtre-type-contrat" className={LABEL}>Type de contrat</label>
                        <select id="filtre-type-contrat" value={filtres.typeContrat} onChange={(e) => setFiltres({...filtres, typeContrat: e.target.value})} className={INPUT}>
                          <option value="all">Tous</option>
                          {typesContrat.map(tc => (
                            <option key={tc.idtype_contrat} value={tc.idtype_contrat}>{tc.nom}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <span id="filtre-statut-label" className={LABEL}>Statut</span>
                        <div role="radiogroup" aria-labelledby="filtre-statut-label" className="grid grid-cols-3 gap-2">
                          {[
                            { v: 'all', l: 'Tous' },
                            { v: 'actif', l: 'Actifs' },
                            { v: 'inactif', l: 'Inactifs' },
                          ].map((o) => {
                            const actif = filtres.statut === o.v;
                            return (
                              <button
                                key={o.v}
                                type="button"
                                role="radio"
                                aria-checked={actif}
                                onClick={() => setFiltres({...filtres, statut: o.v})}
                                className={`rounded-xl border py-2.5 text-sm font-medium transition-all duration-200 ${
                                  actif
                                    ? 'border-blue-600 bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                {o.l}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {configTab === 'format' && (
                  <div>
                    <div className="mb-4 flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100"><Download className="h-4 w-4" /></span>
                      <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Format d'export</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setTypeExport('pdf')}
                        aria-pressed={typeExport === 'pdf'}
                        className={`flex flex-col items-center gap-2 rounded-2xl border-2 px-4 py-5 transition-all duration-200 ${
                          typeExport === 'pdf'
                            ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-700 shadow-md'
                            : 'border-slate-200 text-slate-600 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        <FileText className="h-8 w-8" />
                        <span className="font-semibold">PDF</span>
                        <span className="text-center text-[11px] leading-tight text-slate-500">Idéal pour l'impression et l'archivage</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setTypeExport('excel')}
                        aria-pressed={typeExport === 'excel'}
                        className={`flex flex-col items-center gap-2 rounded-2xl border-2 px-4 py-5 transition-all duration-200 ${
                          typeExport === 'excel'
                            ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 text-green-700 shadow-md'
                            : 'border-slate-200 text-slate-600 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        <FileSpreadsheet className="h-8 w-8" />
                        <span className="font-semibold">Excel</span>
                        <span className="text-center text-[11px] leading-tight text-slate-500">Idéal pour l'analyse et le retraitement</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Zone principale - Rapport personnalisé uniquement */}
          <div className="min-w-0 space-y-6">
            {/* En-tête du constructeur personnalisé */}
            <div className="paie-hero paie-fade-up relative overflow-hidden rounded-2xl px-6 py-5 text-white shadow-xl shadow-blue-900/20" style={{ animationDelay: '180ms' }}>
              <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30 backdrop-blur">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-200">Constructeur</div>
                    <h3 className="text-xl font-bold tracking-tight">Rapport personnalisé</h3>
                    <p className="mt-0.5 text-sm text-blue-100/80">Construisez votre rapport en sélectionnant les colonnes souhaitées</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setModalSauvegarde(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/20"
                >
                  <Save className="h-4 w-4" /> Sauvegarder cette configuration
                </button>
              </div>
            </div>

            {configsSauvegardees.length > 0 && (
              <div className="paie-card paie-fade-up p-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100"><Save className="h-4 w-4" /></span>
                    <div>
                      <h3 className="text-[15px] font-bold tracking-tight text-slate-900">Configurations sauvegardées</h3>
                      <p className="text-xs text-slate-500">{configsSauvegardees.length} modèle{configsSauvegardees.length > 1 ? 's' : ''} réutilisable{configsSauvegardees.length > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setModalSauvegarde(true)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50">
                    <Plus className="h-3.5 w-3.5" /> Sauvegarder actuelle
                  </button>
                </div>
                <div className="paie-scroll grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                  {configsSauvegardees.map((config) => (
                    <div key={config.id} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 transition-all hover:border-blue-300 hover:bg-white hover:shadow-sm">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-900">{config.titre}</div>
                        {config.description && <div className="truncate text-xs text-slate-500">{config.description}</div>}
                      </div>
                      <div className="flex items-center gap-0.5 opacity-70 transition-opacity group-hover:opacity-100">
                        <button type="button" onClick={() => handleChargerConfig(config)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600" title="Charger">
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleModifierConfig(config)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-amber-50 hover:text-amber-600" title="Modifier">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleSupprimerConfig(config.id)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500" title="Supprimer">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ConstructeurRapport
              familles={famillesPerso}
              colonnes={colonnesPerso}
              setColonnes={setColonnesPerso}
              titre={titrePerso}
              setTitre={setTitrePerso}
              typeExport={typeExport}
              totaux={totauxPerso}
              setTotaux={setTotauxPerso}
              apercu={apercu}
              chargementApercu={chargementApercu}
              erreurApercu={erreurApercu}
              aPeriode={Boolean(selectedPaie)}
              indisponibles={indisponibles}
            />

            {/* Résultat de la dernière génération */}
            {resultat && (
              <PanneauResultat resultat={resultat} onFermer={() => setResultat(null)} onTelecharger={(format) => telechargerRapport(format, resultat)} />
            )}

            {/* Barre de génération toujours visible */}
            <div className="sticky bottom-4 z-30">
              <div className="paie-dock paie-fade-up relative overflow-hidden rounded-2xl p-4 text-white sm:p-5" style={{ animationDelay: '300ms' }}>
                <div className="pointer-events-none absolute -right-10 -top-16 h-48 w-48 rounded-full bg-indigo-400/25 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 left-10 h-40 w-40 rounded-full bg-sky-400/15 blur-3xl" />
                <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
                      <LayoutGrid className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200/80">Résumé de la génération</div>
                      <p className="truncate text-sm font-semibold">
                        {selectedPaie ? `Période : ${selectedPaie.libelle_paie}` : 'Aucune période sélectionnée'}
                        {colonnesPerso.length > 0 && ` • ${colonnesPerso.length} colonne${colonnesPerso.length > 1 ? 's' : ''}`}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                        <span className="rounded-full bg-white/10 px-2.5 py-0.5 font-semibold ring-1 ring-white/15">{formatLibelle}</span>
                        <span className="rounded-full bg-white/10 px-2.5 py-0.5 font-semibold ring-1 ring-white/15">
                          {nbFiltres > 0 ? `${nbFiltres} filtre${nbFiltres > 1 ? 's' : ''} actif${nbFiltres > 1 ? 's' : ''}` : 'Aucun filtre'}
                        </span>
                        {codeFiliale && !isConsolidated && (
                          <span className="rounded-full bg-white/25 px-2.5 py-0.5 font-bold tracking-wide">{codeFiliale}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 md:w-72">
                    <button
                      onClick={handleGenerate}
                      disabled={!selectedPaie || !pret || generating}
                      className={`flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold transition-all duration-200 ${
                        !selectedPaie || !pret || generating
                          ? 'cursor-not-allowed bg-white/15 text-white/50'
                          : 'paie-shine bg-white text-blue-700 shadow-lg shadow-black/25 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0'
                      }`}
                    >
                      {generating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Génération en cours...</span>
                        </>
                      ) : (
                        <>
                          <Printer className="h-5 w-5" />
                          <span>Générer le rapport</span>
                        </>
                      )}
                    </button>
                    {generating ? (
                      <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-white/20" aria-hidden="true">
                        <div className="paie-slide h-full w-1/3 rounded-full bg-white" />
                      </div>
                    ) : manquants.length > 0 ? (
                      <p className="mt-2 text-center text-[11px] text-blue-100/70">Sélectionnez {manquants.join(' et ')} pour continuer.</p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Modal de sauvegarde de configuration */}
    {modalSauvegarde && (
      <div className="paie-fade fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={() => setModalSauvegarde(false)}>
        <div className="paie-modal w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/5" onClick={(e) => e.stopPropagation()}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">{configEnEdition ? 'Modifier la configuration' : 'Sauvegarder la configuration'}</h3>
            <button type="button" onClick={() => { setModalSauvegarde(false); setConfigEnEdition(null); setNomConfig(''); setDescriptionConfig(''); }} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label htmlFor="config-nom" className={LABEL}>Nom du rapport *</label>
              <input id="config-nom" type="text" value={nomConfig} onChange={(e) => setNomConfig(e.target.value)} className={INPUT} placeholder="Ex : Salaires mensuels" />
            </div>
            <div>
              <label htmlFor="config-desc" className={LABEL}>Description (optionnel)</label>
              <textarea id="config-desc" value={descriptionConfig} onChange={(e) => setDescriptionConfig(e.target.value)} className={INPUT} rows="2" placeholder="Description optionnelle..." />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => { setModalSauvegarde(false); setConfigEnEdition(null); setNomConfig(''); setDescriptionConfig(''); }} className="rounded-xl bg-slate-100 px-5 py-2.5 text-slate-700 transition-colors hover:bg-slate-200">Annuler</button>
              <button type="button" onClick={handleSauvegarderConfig} className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">{configEnEdition ? 'Mettre à jour' : 'Sauvegarder'}</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ExportEtats;